/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AllProgressCheckStatuses,
  EvidenceDefinition,
  FastTrackLabel,
  FilledInBy,
  INOSSelectCompetence,
  INOSSelectLearningObjectives,
  IProgressCheckLabel,
  IUser,
  IUserProgramRoles,
  LearningObjective,
  Mentor,
  PauseLabel,
  ProgramDataVersionInfo,
  ProgramModuleStatus,
  ProgressCheckStatus,
  ProgressCheckSubmission,
  ProgressState,
  SequentiallyLocked,
  SubmissionButtonState,
  SubmissionState,
} from "@interfaces/Interfaces";
import {
  CompetenceActivityClass,
  ConfidenceLevelData,
  EvidencePart,
  FieldType,
  IClassSubscription,
  IEvidence,
  ProgramData,
  ProgramSkill,
  ProgressCheck,
  ProgressCheckApproval,
} from "@interfaces/Interfaces";
import roles from "@assets/json/roles.json";
import standards from "@assets/json/standards.json";
import _ from "lodash";
import { format, subYears } from "date-fns";
import { IMandatoryStandard, IOptionalStandard } from "@interfaces/Interfaces";
import { ProgramModule, PMUsageData } from "pf-support-lib";
import * as EvidenceUtils from "@utils/evidenceUtils";
import { UserProgramRole } from "@state/UserState";
import {
  AUTOCOMPLETE_PROGRESS_CHECK_COMPETENCY_IDS,
  COMPETENCY_IDS,
  NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS,
  NQP_CONFIDENCE_LEVEL_PROGRESS_CHECK_IDS,
  NQP_WELLBEING_ACTIVITY_IDS,
  PROGRAM_EVIDENCE_PART_IDS,
  PROGRAM_IDS,
  PROGRESS_CHECK_IDS,
  UOC_PROGRAM_EVIDENCE_DEFINITION_IDS,
} from "@constants/programs";

/**
 * Checks if the Progress Check can be completed when the only piece of required Evidence in the Progress Check is completed
 * @param progressCheckCompetenceID
 * @returns
 */
export function autoCompleteProgressCheck(progressCheckCompetenceID: string): boolean {
  return [
    AUTOCOMPLETE_PROGRESS_CHECK_COMPETENCY_IDS.LAS_NQP_OVERVIEW,
    AUTOCOMPLETE_PROGRESS_CHECK_COMPETENCY_IDS.SWAST_PRECEPTORSHIP_OVERVIEW,
    AUTOCOMPLETE_PROGRESS_CHECK_COMPETENCY_IDS.SWAST_PRECEPTORSHIP_PRECEPTOR_SHIFT,
    AUTOCOMPLETE_PROGRESS_CHECK_COMPETENCY_IDS.SWAST_PRECEPTORSHIP_VALIDATION_LOG,
    AUTOCOMPLETE_PROGRESS_CHECK_COMPETENCY_IDS.SWAST_NQP_FEEDBACK_FOR_EL,
    AUTOCOMPLETE_PROGRESS_CHECK_COMPETENCY_IDS.NEAS_NQP_OVERVIEW_SITE,
    AUTOCOMPLETE_PROGRESS_CHECK_COMPETENCY_IDS.NEAS_NQP_OVERVIEW_DOCS,
    AUTOCOMPLETE_PROGRESS_CHECK_COMPETENCY_IDS.NWAS_NQP_MTS_TRAINING,
    AUTOCOMPLETE_PROGRESS_CHECK_COMPETENCY_IDS.YAS_NQP_OVERVIEW,
  ].includes(progressCheckCompetenceID);
}

/**
 *
 * @param submissionState - The ProgressCheckSubmission object for the current progress check
 * @param progressCheck - The ProgressCheck object (optional, used for KeepOpenOnCompletion check)
 * @param allProgressCheckData - All progress check data (optional, used for program completion check)
 * @param allEvidence - All evidence (optional, used for program completion check)
 * @param programID - The program ID (optional, used for program completion check)
 * @param allProgramProgressChecks - All progress checks for the program (used for program completion check)
 * @returns If the user can edit the evidence added to their progress check based on its submission status
 */
export function canAddOrEditProgressCheckEvidence(
  submissionState: ProgressCheckSubmission | undefined,
  progressCheck?: ProgressCheck,
  allProgressCheckData?: AllProgressCheckStatuses[] | null,
  allEvidence?: IEvidence[],
  programID?: string,
  allProgramProgressChecks?: ProgressCheck[]
): boolean {
  // If progress check is completed and has KeepOpenOnCompletion, allow editing unless program is complete

  if (submissionState?.status === SubmissionState.Completed && progressCheck?.KeepOpenOnCompletion) {
    // Check if program is complete (for NQP programs)
    if (programID && isNQPProgram(programID) && allProgressCheckData && allEvidence && allProgramProgressChecks) {
      return !isProgramCompleteNQP(allProgramProgressChecks, allProgressCheckData, allEvidence, programID);
    }
    // For non-NQP programs, allow editing if KeepOpenOnCompletion is true
    return true;
  }

  if (programID && isNQPProgram(programID) && allProgressCheckData && allEvidence && allProgramProgressChecks) {
    if (
      submissionState?.status !== SubmissionState.Approved &&
      submissionState?.status !== SubmissionState.Submitted &&
      submissionState?.status !== SubmissionState.Completed &&
      submissionState?.status !== SubmissionState.Rejected
    ) {
      return !isProgramCompleteNQP(allProgramProgressChecks, allProgressCheckData, allEvidence, programID);
    }
  }

  // Original logic for other statuses
  return (
    submissionState?.status !== SubmissionState.Approved &&
    submissionState?.status !== SubmissionState.Submitted &&
    submissionState?.status !== SubmissionState.Completed &&
    submissionState?.status !== SubmissionState.Rejected
  );
}

/**
 *
 * @param approval - The ProgressCheckApproval enum for the current progress check
 * @returns Whether the progress check requires approval
 */
export function canSubmitProgressCheck(approval: ProgressCheckApproval): boolean {
  const approvalRequired = [
    ProgressCheckApproval.ApprovalOO,
    ProgressCheckApproval.Required,
    ProgressCheckApproval.RequiredExtension,
    ProgressCheckApproval.RequiredRejection,
  ];

  return approvalRequired.includes(approval);
}

/**
 *
 * @param selectedLearningObjectives - All the user's selected Learning Objectives
 * @param learningObjectives - All available Learning Objectives
 * @returns
 */
export function checkAllLearningObjectivesSelected(
  selectedLearningObjectives: INOSSelectLearningObjectives[],
  learningObjectives: LearningObjective[]
): boolean {
  if (learningObjectives.length > 0) {
    const ids = selectedLearningObjectives.map((learningObjective) => learningObjective.id);
    const array = learningObjectives.filter((learningObjective) => !ids.includes(learningObjective.ID));

    return array.length === 0;
  }

  return false;
}

/**
 * Utility function to check if the CompetenceActivity of a Program Module is populated
 * @param key - The key to check for
 * @param object - The object to check
 * @returns false if the object is a string, otherwise returns whether the key exists in the object
 */
function checkCompetenceActivityType<T extends object>(key: PropertyKey, object: T | string): key is keyof T {
  if (typeof object === "string") {
    return false;
  }

  return key in object;
}

export function progressCheckHasMentorAddedEvidence(progressCheck: ProgressCheck): boolean {
  const progressCheckCompetences = progressCheck?.["Competence/Activity"] ?? [];

  const competencesAddedByMentor = progressCheckCompetences.filter((competence) => {
    return competence.FilledInBy === FilledInBy.Mentor;
  });

  return competencesAddedByMentor.length > 0;
}

/**
 * Utility function to check if the mentor has added evidence to all the competences they are responsible for in a given progress check
 * @param progressCheck - The ProgressCheck object
 * @param allEvidence - All the user's evidence
 * @returns true if the mentor has added evidence to all the competences they are responsible for, false otherwise
 */
export function checkIfAllMentorEvidenceIsAdded(progressCheck: ProgressCheck, allEvidence: IEvidence[]): boolean {
  const progressCheckCompetences = progressCheck?.["Competence/Activity"] ?? [];

  const competencesAddedByMentor = progressCheckCompetences.filter((competence) => {
    return competence.FilledInBy === FilledInBy.Mentor;
  });

  const mappedIDs = competencesAddedByMentor.map((competence) => competence.ID);

  const competencesAddedByMentee = progressCheckCompetences.filter((competence) => {
    return competence.FilledInBy !== FilledInBy.Mentor;
  });

  const requiredCompetencesAddedByMentee = competencesAddedByMentee.filter((competence) => {
    return competence.isLockedByRequiredEvidence;
  });

  if (requiredCompetencesAddedByMentee.length === 0) return false;

  const mappedMenteeIDs = competencesAddedByMentee.map((competence) => competence.ID);

  const evidenceAddedByMentee = allEvidence.filter((evidence) => {
    const evidenceJSON = evidence.evidenceJSON;
    const progressCheckID = evidenceJSON?.programInfo?.progressCheckID ?? "";
    const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID ?? "";

    const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

    return (
      progressCheckID === progressCheck?.ID &&
      mappedMenteeIDs.includes(progressCheckCompetenceID) &&
      onHoldReset === 0 &&
      evidence.draft !== true
    );
  });

  const evidenceAddedByMentor = allEvidence.filter((evidence) => {
    const evidenceJSON = evidence.evidenceJSON;
    const progressCheckID = evidenceJSON?.programInfo?.progressCheckID ?? "";
    const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID ?? "";

    const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

    return (
      progressCheckID === progressCheck?.ID &&
      mappedIDs.includes(progressCheckCompetenceID) &&
      onHoldReset === 0 &&
      evidence.draft !== true
    );
  });

  if (mappedIDs.length > 0) {
    if (competencesAddedByMentee.length > 0 && evidenceAddedByMentee.length > 0) {
      // Return true to lock if mentee has added all their evidence but mentor hasn't added all theirs
      return evidenceAddedByMentee.length >= competencesAddedByMentee.length;
    }

    return mappedIDs.length !== evidenceAddedByMentor.length;
  }

  return false;
}

export function useNewMenteeEvidenceImplementation(programID: string): boolean {
  return [PROGRAM_IDS.LAS_NQP, PROGRAM_IDS.NWAS_NQP].includes(programID);
}

/**
 * Utility function to check if the mentee can add their evidence based on mentor evidence completion
 * @param progressCheck - The ProgressCheck object
 * @param allEvidence - All the user's evidence
 * @returns true if the mentee can add their evidence (mentor has completed required evidence), false otherwise
 */
export function checkIfMenteeCanAddEvidence(
  progressCheck: ProgressCheck,
  allEvidence: IEvidence[],
  useNewImplementation: boolean = false
): boolean {
  if (!useNewImplementation) {
    return checkIfAllMentorEvidenceIsAdded(progressCheck, allEvidence);
  }

  const progressCheckCompetences = progressCheck?.["Competence/Activity"] ?? [];

  const competencesAddedByMentor = progressCheckCompetences.filter((competence) => {
    return competence.FilledInBy === FilledInBy.Mentor;
  });

  const competencesAddedByMentee = progressCheckCompetences.filter((competence) => {
    return competence.FilledInBy !== FilledInBy.Mentor;
  });

  // If there are no mentor competences, mentee can always add evidence
  if (competencesAddedByMentor.length === 0) {
    return false; // mentee can add evidence, so don't disable button
  }

  // If there are no mentee competences, return true (nothing for mentee to add)
  if (competencesAddedByMentee.length === 0) {
    return true; // disable button when no evidence can be added
  }

  // Get evidence already added by mentee
  const evidenceAddedByMentee = allEvidence.filter((evidence) => {
    const evidenceJSON = evidence.evidenceJSON;
    const progressCheckID = evidenceJSON?.programInfo?.progressCheckID ?? "";
    const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID ?? "";
    const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

    return (
      progressCheckID === progressCheck?.ID &&
      competencesAddedByMentee.some((comp) => comp.ID === progressCheckCompetenceID) &&
      onHoldReset === 0 &&
      evidence.draft !== true
    );
  });

  // Check which competences still need evidence to be added
  const competencesThatNeedEvidence = competencesAddedByMentee.filter((competence) => {
    const evidenceForThisCompetence = evidenceAddedByMentee.filter((evidence) => {
      const evidenceJSON = evidence.evidenceJSON;
      return evidenceJSON?.programInfo?.progressCheckCompetenceID === competence.ID;
    });

    const requiredCount = competence["Number Evidences required"] ?? 0;
    return evidenceForThisCompetence.length < requiredCount;
  });

  const requiredCompetencesThatNeedEvidence = competencesThatNeedEvidence.filter((competence) => {
    // Required evidence: Number Evidences required > 0 AND not locked by required evidence
    return (competence["Number Evidences required"] ?? 0) > 0 && !competence.isLockedByRequiredEvidence;
  });

  // Optional competences that need evidence: Number Evidences required = 0, not locked, and no evidence added yet
  const optionalCompetencesThatNeedEvidence = competencesThatNeedEvidence.filter((competence) => {
    const evidenceForThisCompetence = evidenceAddedByMentee.filter((evidence) => {
      const evidenceJSON = evidence.evidenceJSON;
      return evidenceJSON?.programInfo?.progressCheckCompetenceID === competence.ID;
    });
    return (
      (competence["Number Evidences required"] ?? 0) === 0 &&
      !competence.isLockedByRequiredEvidence &&
      evidenceForThisCompetence.length === 0
    );
  });

  const lockedRequiredCompetencesThatNeedEvidence = competencesThatNeedEvidence.filter((competence) => {
    // Locked required evidence: locked by required evidence AND Number Evidences required > 0
    return competence.isLockedByRequiredEvidence && (competence["Number Evidences required"] ?? 0) > 0;
  });

  // Locked optional competences that need evidence: locked by required evidence, Number Evidences required = 0, and no evidence added yet
  const lockedOptionalCompetencesThatNeedEvidence = competencesAddedByMentee.filter((competence) => {
    const evidenceForThisCompetence = evidenceAddedByMentee.filter((evidence) => {
      const evidenceJSON = evidence.evidenceJSON;
      return evidenceJSON?.programInfo?.progressCheckCompetenceID === competence.ID;
    });
    return (
      competence.isLockedByRequiredEvidence &&
      (competence["Number Evidences required"] ?? 0) === 0 &&
      evidenceForThisCompetence.length === 0
    );
  });

  // Get evidence added by mentor
  const evidenceAddedByMentor = allEvidence.filter((evidence) => {
    const evidenceJSON = evidence.evidenceJSON;
    const progressCheckID = evidenceJSON?.programInfo?.progressCheckID ?? "";
    const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID ?? "";
    const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

    return (
      progressCheckID === progressCheck?.ID &&
      competencesAddedByMentor.some((comp) => comp.ID === progressCheckCompetenceID) &&
      onHoldReset === 0 &&
      evidence.draft !== true
    );
  });

  // Check if mentor has added all their required evidence
  const mentorCompetenceIDs = competencesAddedByMentor.map((comp) => comp.ID);
  const mentorEvidenceCompetenceIDs = evidenceAddedByMentor.map(
    (evidence) => evidence.evidenceJSON?.programInfo?.progressCheckCompetenceID
  );

  const mentorHasAddedAllEvidence = mentorCompetenceIDs.every((id) => mentorEvidenceCompetenceIDs.includes(id));

  // If mentee has required evidence to add, allow them to add it regardless of mentor status
  if (requiredCompetencesThatNeedEvidence.length > 0) {
    return false; // mentee can add required evidence, so don't disable button
  }

  // If mentee has optional evidence that needs to be added, allow them to add it regardless of mentor status
  if (optionalCompetencesThatNeedEvidence.length > 0) {
    return false; // mentee can add optional evidence, so don't disable button
  }

  // If mentee has locked required evidence that needs to be added, check if mentor has completed their evidence
  if (lockedRequiredCompetencesThatNeedEvidence.length > 0) {
    return !mentorHasAddedAllEvidence; // disable if mentor hasn't completed
  }

  // If mentee has locked optional evidence that needs to be added, allow them to add it only if mentor has completed their evidence
  if (lockedOptionalCompetencesThatNeedEvidence.length > 0) {
    if (mentorHasAddedAllEvidence) {
      return false; // enable button
    } else {
      return true; // disable button until mentor completes
    }
  }

  // If any evidence can be added, enable the button
  const canAddEvidence =
    requiredCompetencesThatNeedEvidence.length > 0 ||
    optionalCompetencesThatNeedEvidence.length > 0 ||
    lockedOptionalCompetencesThatNeedEvidence.length > 0;

  if (canAddEvidence) {
    return false; // enable button
  }

  // Otherwise, disable the button
  return true;
}

export function progressCheckAddNewEvidenceButtonDisabled(
  progressCheck: ProgressCheck,
  progressChecks: ProgressCheck[],
  programEvidence: IEvidence[],
  programProgressCheckData: AllProgressCheckStatuses | null,
  programID: string,
  fastTrackActive: boolean,
  pauseAndHold: boolean
): boolean {
  if (isNQPProgram(programID)) {
    if (pauseAndHold) {
      return true;
    }

    if (
      progressCheck?.SequentiallyLocked !== undefined &&
      [`${SequentiallyLocked.Unlocked}`, `${SequentiallyLocked.UnlockedIgnore}`].includes(
        progressCheck.SequentiallyLocked
      )
    ) {
      return false;
    }

    const progressCheckIndex = progressChecks.findIndex((item) => item.ID === progressCheck.ID);

    let previousCheckIndex;

    if (progressCheck?.UnlockedByID) {
      previousCheckIndex = progressChecks?.findIndex((item) => item.ID === progressCheck.UnlockedByID);
    }

    let previousProgressCheck: ProgressCheck | undefined;

    if (previousCheckIndex) {
      previousProgressCheck = progressChecks[previousCheckIndex];
    } else if (progressCheckIndex > 0) {
      previousProgressCheck = progressChecks[progressCheckIndex - 1];
    }

    if (!previousProgressCheck) {
      if (fastTrackActive) {
        return false;
      }

      if (!progressCheckHasMentorAddedEvidence(progressCheck)) {
        const result = isAllRequiredEvidenceAddedForProgressCheck(
          programEvidence,
          progressCheck["Competence/Activity"],
          true
        );

        return result;
      }

      return false;
    }

    if (isProgressCheckApprovedBasedOnCompTrigger(programEvidence, progressCheck["Competence/Activity"])) {
      return false;
    }

    let previousProgressCheckData: ProgressCheckStatus | undefined;

    if (programProgressCheckData) {
      previousProgressCheckData = programProgressCheckData.pCs.find((item) => item.pCId === previousProgressCheck!.ID);
    }

    if (previousProgressCheck.Approval === ProgressCheckApproval.NotRequired) {
      if (fastTrackActive) {
        return false;
      }

      const previousProgressCheckLocked = checkIfProgressCheckIsLocked(
        previousProgressCheck,
        progressChecks,
        programEvidence,
        programProgressCheckData,
        programID,
        fastTrackActive,
        pauseAndHold
      );

      // Check if previous progress check is locked - used when previous progress check is sequentially locked but doesn't require approval
      if (previousProgressCheckLocked) {
        if (previousProgressCheckData) {
          if (
            previousProgressCheckData.submissions.status === SubmissionState.Approved ||
            previousProgressCheckData.submissions.status === SubmissionState.Completed
          ) {
            return false;
          }
        }

        return true;
      }

      const previousProgressCheckEvidence = getEvidenceForProgressCheck(programEvidence, previousProgressCheck.ID);

      const result = !isAllRequiredEvidenceAddedForProgressCheck(
        previousProgressCheckEvidence,
        previousProgressCheck["Competence/Activity"],
        true
      );

      return result;
    }

    const currentProgressCheckData = programProgressCheckData?.pCs.find((item) => item.pCId === progressCheck.ID);

    if (currentProgressCheckData) {
      if (
        currentProgressCheckData.submissions.status === SubmissionState.Approved ||
        currentProgressCheckData.submissions.status === SubmissionState.Completed ||
        currentProgressCheckData.submissions.status === SubmissionState.Submitted
      ) {
        if (progressCheck.KeepOpenOnCompletion) {
          return false;
        }

        return true;
      }
    }

    if (fastTrackActive) {
      return false;
    }

    if (previousProgressCheckData) {
      const progressCheckIsLockedBasedOnCompetenceTrigger = progressCheck.UnlockedByCompetenceID;

      if (progressCheckIsLockedBasedOnCompetenceTrigger) {
        const isUnlockedBasedOnCompetenceTrigger = isProgressCheckUnlockedBasedOnCompetenceTrigger(
          programEvidence,
          progressCheck,
          previousProgressCheck,
          previousProgressCheck["Competence/Activity"]
        );

        return !isUnlockedBasedOnCompetenceTrigger;
      }

      return previousProgressCheckData.submissions.status !== SubmissionState.Approved;
    }

    return true;
  } else {
    return checkIfProgressCheckIsLocked(
      progressCheck,
      progressChecks,
      programEvidence,
      programProgressCheckData,
      programID,
      fastTrackActive,
      pauseAndHold
    );
  }
}

/**
 *
 * @param progressCheck - The Progress Check to check
 * @param progressChecks - All Progress Checks in the Program Module
 * @param programEvidence - All the user's Evidence for the Program Module
 * @param programProgressCheckData - The user's Progress Check Data for the current Program Module
 * @returns
 */
export function checkIfProgressCheckIsLocked(
  progressCheck: ProgressCheck,
  progressChecks: ProgressCheck[],
  programEvidence: IEvidence[],
  programProgressCheckData: AllProgressCheckStatuses | null,
  programID: string,
  fastTrackActive: boolean,
  pauseAndHold: boolean
): boolean {
  if (isPreceptorship(programID) || isNQPProgram(programID)) {
    if (pauseAndHold) {
      return true;
    }

    const currentProgressCheckData = programProgressCheckData?.pCs.find((item) => item.pCId === progressCheck.ID);

    if (currentProgressCheckData) {
      if (
        currentProgressCheckData.submissions.status === SubmissionState.Approved ||
        currentProgressCheckData.submissions.status === SubmissionState.Completed ||
        currentProgressCheckData.submissions.status === SubmissionState.Submitted ||
        currentProgressCheckData.submissions.status === SubmissionState.Rejected
      ) {
        if (progressCheck.KeepOpenOnCompletion) {
          return false;
        }

        return true;
      }
    }

    if (
      progressCheck.SequentiallyLocked !== undefined &&
      [`${SequentiallyLocked.Unlocked}`, `${SequentiallyLocked.UnlockedIgnore}`].includes(
        progressCheck.SequentiallyLocked
      )
    ) {
      return false;
    }

    const progressCheckIndex = progressChecks.findIndex((item) => item.ID === progressCheck.ID);

    let previousCheckIndex;

    if (progressCheck.UnlockedByID) {
      previousCheckIndex = progressChecks.findIndex((item) => item.ID === progressCheck.UnlockedByID);
    }

    let previousProgressCheck: ProgressCheck | undefined;

    if (previousCheckIndex) {
      previousProgressCheck = progressChecks[previousCheckIndex];
    } else if (progressCheckIndex > 0) {
      previousProgressCheck = progressChecks[progressCheckIndex - 1];
    }

    if (!previousProgressCheck) {
      if (fastTrackActive) {
        return false;
      }

      return false;
    }

    const progressCheckIsLockedBasedOnCompetenceTrigger = progressCheck.UnlockedByCompetenceID;

    if (progressCheckIsLockedBasedOnCompetenceTrigger) {
      const isUnlockedBasedOnCompetenceTrigger = isProgressCheckUnlockedBasedOnCompetenceTrigger(
        programEvidence,
        progressCheck,
        previousProgressCheck,
        previousProgressCheck["Competence/Activity"]
      );

      return !isUnlockedBasedOnCompetenceTrigger;
    }

    if (isProgressCheckApprovedBasedOnCompTrigger(programEvidence, previousProgressCheck["Competence/Activity"])) {
      return false;
    }

    let previousProgressCheckData: ProgressCheckStatus | undefined;

    if (programProgressCheckData) {
      previousProgressCheckData = programProgressCheckData.pCs.find((item) => item.pCId === previousProgressCheck!.ID);
    }

    if (previousProgressCheck.Approval === ProgressCheckApproval.NotRequired) {
      if (fastTrackActive) {
        return false;
      }

      const previousProgressCheckLocked = checkIfProgressCheckIsLocked(
        previousProgressCheck,
        progressChecks,
        programEvidence,
        programProgressCheckData,
        programID,
        fastTrackActive,
        pauseAndHold
      );

      // Check if previous progress check is locked - used when previous progress check is sequentially locked but doesn't require approval
      if (previousProgressCheckLocked) {
        if (previousProgressCheckData) {
          if (
            previousProgressCheckData.submissions.status === SubmissionState.Approved ||
            previousProgressCheckData.submissions.status === SubmissionState.Completed
          ) {
            return false;
          }
        }

        return true;
      }

      const previousProgressCheckEvidence = getEvidenceForProgressCheck(programEvidence, previousProgressCheck.ID);

      const result = !isAllRequiredEvidenceAddedForProgressCheck(
        previousProgressCheckEvidence,
        previousProgressCheck["Competence/Activity"],
        true
      );

      return result;
    }

    if (fastTrackActive) {
      return false;
    }

    if (previousProgressCheckData) {
      return previousProgressCheckData.submissions.status !== SubmissionState.Approved;
    }

    return true;
  } else {
    if (
      progressCheck.SequentiallyLocked !== undefined &&
      [`${SequentiallyLocked.Unlocked}`, `${SequentiallyLocked.UnlockedIgnore}`].includes(
        progressCheck.SequentiallyLocked
      )
    ) {
      return false;
    }

    const progressCheckIndex = progressChecks.findIndex((item) => item.ID === progressCheck.ID);

    let previousCheckIndex;

    if (progressCheck.UnlockedByID) {
      previousCheckIndex = progressChecks.findIndex((item) => item.ID === progressCheck.UnlockedByID);
    }

    let previousProgressCheck: ProgressCheck | undefined;

    if (previousCheckIndex) {
      previousProgressCheck = progressChecks[previousCheckIndex];
    } else if (progressCheckIndex > 0) {
      previousProgressCheck = progressChecks[progressCheckIndex - 1];
    }

    if (!previousProgressCheck) {
      return false;
    }

    if (isProgressCheckApprovedBasedOnCompTrigger(programEvidence, previousProgressCheck["Competence/Activity"])) {
      return false;
    }

    if (previousProgressCheck.Approval === ProgressCheckApproval.NotRequired) {
      // Check if previous progress check is locked - used when previous progress check is sequentially locked but doesn't require approval
      if (
        checkIfProgressCheckIsLocked(
          previousProgressCheck,
          progressChecks,
          programEvidence,
          programProgressCheckData,
          programID,
          false,
          false
        )
      ) {
        return true;
      }

      const previousProgressCheckEvidence = getEvidenceForProgressCheck(programEvidence, previousProgressCheck.ID);

      const result = !isAllRequiredEvidenceAddedForProgressCheck(
        previousProgressCheckEvidence,
        previousProgressCheck["Competence/Activity"]
      );
      return result;
    }

    let previousProgressCheckData;

    if (programProgressCheckData) {
      previousProgressCheckData = programProgressCheckData.pCs.find((item) => item.pCId === previousProgressCheck!.ID);
    }

    if (previousProgressCheckData) {
      return previousProgressCheckData.submissions.status !== SubmissionState.Approved;
    }

    return true;
  }
}

/**
 * Checks whether the user has a ProgressCheckStatus for the current progress check
 * @param progressCheckData - The current ProgressCheckStatus object to check
 * @param progressCheckID - The ID of the progress check
 * @returns true if the object exists and its pCId is equal to the progressCheckID
 */
export function checkIfSubmissionExists(
  progressCheckData: ProgressCheckStatus | null,
  progressCheckID: string
): boolean {
  if (progressCheckData) {
    return progressCheckData.pCId === progressCheckID;
  }

  return false;
}

/**
 *
 * @param progressCheckCompetenceID - The ID of the current competence
 * @returns true if the ID is included in the list of competences for the confidence level entry
 */
export function competenceIsConfidenceLevel(progressCheckCompetenceID: string): boolean {
  return [
    // SWASFT Preceptorship
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.SWAST_PRECEPTORSHIP_PRE,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.SWAST_PRECEPTORSHIP_7_SHIFTS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.SWAST_PRECEPTORSHIP_14_SHIFTS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.SWAST_PRECEPTORSHIP_END,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.SWAST_PRECEPTORSHIP_GENERAL,
    // NEAS NQP
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.NEAS_NQP_0_6_MONTHS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.NEAS_NQP_6_12_MONTHS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.NEAS_NQP_12_18_MONTHS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.NEAS_NQP_18_24_MONTHS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.NEAS_NQP_GENERAL,
    // YAS NQP
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.YAS_NQP_0_6_MONTHS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.YAS_NQP_6_12_MONTHS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.YAS_NQP_12_18_MONTHS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.YAS_NQP_18_24_MONTHS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.YAS_NQP_TL_SHIFT,
    // LAS NQP
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.LAS_NQP_6_MONTH_REVIEW,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.LAS_NQP_12_MONTHS_REVIEW,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.LAS_NQP_18_MONTHS_REVIEW,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.LAS_NQP_24_MONTHS_REVIEW,
  ].includes(progressCheckCompetenceID);
}

/**
 * Checks whether the button to add Program Module evidence should be disabled in the FAB popover
 * @param program
 * @param allEvidence
 * @param user
 * @returns
 */
export function disableFABProgramButton(
  program: ProgramData,
  allProgressCheckData: AllProgressCheckStatuses[] | undefined | null,
  allEvidence: IEvidence[],
  user: any,
  fastTrackActive: boolean,
  pauseAndHold: boolean
): boolean {
  if (isPreceptorship(program.ID) || isNQPProgram(program.ID)) {
    if (hasSetupNQP(program.ID) && !isNQPDetailsSet(user, program.ID)) return true;
    if (isSPAPRP(program.ID) && !isSPAPRPDetailsSet(user)) return true;

    const progressChecks = program.ProgressChecks ?? [];

    return (
      isProgramCompleteNQP(progressChecks, allProgressCheckData, allEvidence, program.ID) ||
      removeProgressChecks(
        progressChecks,
        allEvidence,
        program,
        allProgressCheckData ?? [],
        fastTrackActive,
        pauseAndHold
      ).length === 0
    );
  } else if (isNwasEPRR(program.ID)) {
    return !isNwasEprrDetailsSet(user);
  } else if (isSwastEPRR(program.ID)) {
    return !isSwastEprrDetailsSet(user);
  } else if (isNeasEPRR(program.ID)) {
    return !isNeasEprrDetailsSet(user);
  } else if (isTestEPRR(program.ID)) {
    return !isTestEprrDetailsSet(user);
  } else if (isMTS(program.ID)) {
    return !isMtsDetailsSet(user);
  } else if (isCHUB(program.ID)) {
    return !isChubDetailsSet(user);
  } else if (isCSM(program.ID)) {
    return !isCsmDetailsSet(user);
  } else if (isClinicalAdvisor(program.ID)) {
    return !isClinicalAdvisorsDetailsSet(user);
  } else if (isSPAPRP(program.ID)) {
    return !isSPAPRPDetailsSet(user);
  } else if (isNwasARC(program.ID)) {
    return !isNwasARCDetailsSet(user);
  } else if (isUOCApprenticeship(program.ID)) {
    const userLevel = getProgramLevel(user?.programRoles ?? [], program.ID) ?? "";
    return isUOCProgramLockedByEoYReview(
      program.ID,
      program.ProgressChecks ?? [],
      allProgressCheckData,
      allEvidence,
      userLevel
    );
  }

  return false;
}

export function isSwastOrNwasEprrProgram(programID?: string): boolean {
  return programID === PROGRAM_IDS.NWAS_EPRR || programID === PROGRAM_IDS.SWAST_EPRR;
}

/**
 * Checks whether to display the Progress Checks page in the PDF Export
 *
 * SWAST Preceptorship NQP1
 * @param programID - The ID of the current Program Module
 * @returns
 */
export function displayProgressChecksInPDF(programID: string = ""): boolean {
  return [
    PROGRAM_IDS.LAS_NQP,
    PROGRAM_IDS.NEAS_NQP,
    PROGRAM_IDS.NWAS_NQP,
    PROGRAM_IDS.SWAST_PRECEPTORSHIP,
    PROGRAM_IDS.SWAST_NQP2,
    PROGRAM_IDS.SWAST_SP_AP_RP,
    PROGRAM_IDS.YAS_NQP,
    PROGRAM_IDS.UOC_APPRENTICESHIP,
  ].includes(programID);
}

/**
 * Checks whether to display the NOS Standards page in the PDF Export - NWAS EPRR
 *
 * NWAS EPRR Evidence
 * @param programID - The ID of the current Program Module
 * @returns
 */
export function displayStandardsInPDF(programID: string = ""): boolean {
  return [PROGRAM_IDS.NWAS_EPRR, PROGRAM_IDS.SWAST_EPRR, PROGRAM_IDS.NEAS_EPRR, PROGRAM_IDS.TEST_PROGRAM].includes(
    programID
  );
}

/**
 * Filters the input list of programIDs based on if they include progress checks
 * @param programIDs
 * @returns a filtered array of programIDs
 */
export function filterProgressCheckProgramIDs(programIDs: string[]): string[] {
  const progressCheckProgramIDs = [
    PROGRAM_IDS.LAS_CA,
    PROGRAM_IDS.LAS_CSM,
    PROGRAM_IDS.LAS_CHUB,
    PROGRAM_IDS.LAS_MTS,
    PROGRAM_IDS.LAS_NQP,
    PROGRAM_IDS.NEAS_NQP,
    PROGRAM_IDS.NWAS_NQP,
    PROGRAM_IDS.SWAST_PRECEPTORSHIP,
    PROGRAM_IDS.SWAST_NQP2,
    PROGRAM_IDS.SWAST_SP_AP_RP,
    PROGRAM_IDS.YAS_NQP,
    PROGRAM_IDS.UOC_APPRENTICESHIP,
  ];
  return programIDs.filter((item) => progressCheckProgramIDs.includes(item));
}

/**
 *  Formats the approval text using the name of the mentor who approved the progress check and the time they approved it
 * @param input - The text to format
 * @param submissionStatus - The ProgressCheckSubmission object
 * @returns The formatted string - e.g. "Test User approved this progress check for you at 16:39 on 15 Mar 2024." | An empty string if no input or submissionStatus is present
 */
export function formatApprovalText(
  input: string | undefined,
  submissionStatus: ProgressCheckSubmission | undefined
): string {
  let result = "";

  if (input && submissionStatus) {
    const mentorInfo = submissionStatus.history[0];
    const mentorName = mentorInfo.displayName;
    const date = new Date(mentorInfo.date);

    result = input.replace("[MENTOR]", `<b>${mentorName}</b>`);
    result = result.replace("[DATE]", `<b>${format(date, "H:mm")} on ${format(date, "d MMM yyyy")}</b>`);
  }

  return result;
}

/**
 * Formats the rejection text using the name of the mentor who rejected the progress check and the time they rejected it
 * @param input - The text to format
 * @param submissionStatus - The ProgressCheckSubmission object
 * @returns The formatted string - e.g. "Test User rejected this progress check for you at 16:39 on 15 Mar 2024." | An empty string if no input or submissionStatus is present
 */
export function formatRejectionText(
  input: string | undefined,
  submissionStatus: ProgressCheckSubmission | undefined
): string {
  let result = "";

  console.log("input", input);
  console.log("submissionStatus", submissionStatus);

  if (input && submissionStatus) {
    const mentorInfo = submissionStatus.history[0];
    const mentorName = mentorInfo.displayName;
    const date = new Date(mentorInfo.date);

    result = input.replace("[MENTOR]", `<b>${mentorName}</b>`);
    result = result.replace("[DATE]", `<b>${format(date, "H:mm")} on ${format(date, "d MMM yyyy")}</b>`);
  }

  return result;
}

export function hasAbsenceConversation(
  allProgressCheckData: AllProgressCheckStatuses[] | undefined | null,
  programID: string
): boolean {
  let result = false;

  if (allProgressCheckData) {
    const programProgressCheckData = allProgressCheckData.find((item) => item.programID === programID);

    if (programProgressCheckData) {
      const metadataProgressCheck = programProgressCheckData.pCs.find((item) => item.pCId === "pMData");

      if (metadataProgressCheck) {
        const pauseData = metadataProgressCheck.pMData?.data;

        if (pauseData?.AbsenceConversation || pauseData?.ActionPlan) {
          result = true;
        }
      }
    }
  }

  return result;
}

export function shouldHidePreceptorsInDetailsCard(programID: string): boolean {
  return [
    PROGRAM_IDS.SWAST_SP_AP_RP, // SWASFT SP/AP/RP
    PROGRAM_IDS.UOC_APPRENTICESHIP, // University of Cumbria
  ].includes(programID);
}

/**
 * Checks whether the currently selected program requires user setup
 * @param programID - The current program ID
 * @returns
 */
export function hasSetup(programID: string): boolean {
  return [
    PROGRAM_IDS.LAS_CA,
    PROGRAM_IDS.LAS_CHUB,
    PROGRAM_IDS.LAS_CSM,
    PROGRAM_IDS.LAS_MTS,
    PROGRAM_IDS.NEAS_EPRR,
    PROGRAM_IDS.NWAS_EPRR,
    PROGRAM_IDS.SWAST_EPRR,
    PROGRAM_IDS.NWAS_ARC,
    PROGRAM_IDS.SWAST_HART_EPRR,
    PROGRAM_IDS.TEST_PROGRAM,
  ].includes(programID);
}

export function hasSetupNQP(programID: string): boolean {
  return [PROGRAM_IDS.LAS_NQP].includes(programID);
}

export function forceUserDetailsSetup(programID: string): boolean {
  const nqpSetup = hasSetupNQP(programID);
  const normalSetup = [PROGRAM_IDS.SWAST_SP_AP_RP].includes(programID);

  return nqpSetup || normalSetup;
}

export function canHandleMultiplePiecesOfEvidencePerEvidenceType(programID: string): boolean {
  return [
    PROGRAM_IDS.LAS_CA,
    PROGRAM_IDS.LAS_CHUB,
    PROGRAM_IDS.LAS_CSM,
    PROGRAM_IDS.LAS_MTS,
    PROGRAM_IDS.LAS_NQP,
    PROGRAM_IDS.NEAS_NQP,
    PROGRAM_IDS.NWAS_NQP,
    PROGRAM_IDS.NWAS_ARC,
    PROGRAM_IDS.SWAST_NQP2,
    PROGRAM_IDS.SWAST_SP_AP_RP,
    PROGRAM_IDS.YAS_NQP,
    PROGRAM_IDS.UOC_APPRENTICESHIP,
  ].includes(programID);
}

export function canAddEvidenceAfterProgressCheckCompletion(progressCheck: ProgressCheck): boolean {
  return progressCheck?.KeepOpenOnCompletion ?? false;
}

export function hideDisclaimerText(programID: string): boolean {
  return [PROGRAM_IDS.LAS_MTS, PROGRAM_IDS.LAS_CHUB, PROGRAM_IDS.LAS_CSM, PROGRAM_IDS.LAS_CA].includes(programID);
}

/**
 * Retrieves all the required skills from the current program list of competences
 * @param allCompetences - All competences for the program
 *
 */
export function getAllRequiredSkillsForProgram(allCompetences: CompetenceActivityClass[]): ProgramSkill[] {
  let skills = allCompetences.map((item) => item.Skills);
  skills = skills.filter((skill) => skill !== undefined);
  const flattenedSkills = _.flatten(skills);

  const required = flattenedSkills.filter(
    (skill: any): skill is ProgramSkill =>
      skill && skill.numberRequiredInDuration && parseInt(skill.numberRequiredInDuration, 10) > 0
  );

  return required;
}

export function skipCompetencePage(
  programID: string,
  competenceDescription: string | undefined,
  competenceID: string
): boolean {
  const programIncluded = [PROGRAM_IDS.SWAST_SP_AP_RP].includes(programID);

  const competenceIncluded = [COMPETENCY_IDS.SWAST_SP_AP_RP_CPD_ENTRY].includes(competenceID);

  const descriptionNotIncluded = !competenceDescription;

  if (programIncluded) {
    return !competenceIncluded;
  }

  return descriptionNotIncluded;
}

export function mapProgramRolesToRoleNames(programRoles: Array<UserProgramRole | string>): string[] {
  if (!programRoles) return [];

  return programRoles.map((role) => {
    if (typeof role === "string") {
      return role;
    }

    return role.roleName;
  });
}

export function getAllLearningObjectivesForProgram(program: ProgramData): LearningObjective[] {
  const allLearningObjectives: LearningObjective[] = [];

  const progressChecks = program.ProgressChecks ?? [];
  const ids = new Set<string>();

  for (const progressCheck of progressChecks) {
    for (const competence of progressCheck["Competence/Activity"]) {
      for (const learningObjective of competence["Learning Objective"]!) {
        if (!ids.has(learningObjective.ID)) {
          allLearningObjectives.push(learningObjective);
          ids.add(learningObjective.ID);
        }
      }
    }
  }

  allLearningObjectives.sort((a, b) => {
    const splitA = a.Name.split(".");
    const splitB = b.Name.split(".");

    const numA = parseInt(splitA[1], 10) ?? 0;
    const numB = parseInt(splitB[1], 10) ?? 0;
    const sectionA = splitA[2] ?? "";
    const sectionB = splitB[2] ?? "";

    if (numA < numB) return -1;
    if (numA > numB) return 1;
    if (numA === numB) {
      if (sectionA.localeCompare(sectionB) < 0) return -1;
      if (sectionA.localeCompare(sectionB) > 0) return 1;
    }

    return 0;
  });

  return allLearningObjectives;
}

export function getAllLearningObjectivesForRole(program: ProgramData, roleName: string): LearningObjective[] {
  let allLearningObjectives: LearningObjective[] = [];

  const programRoles = program.Roles ?? [];
  const role = programRoles.find((role) => role.Name === roleName);

  if (role) {
    const learningObjectives = role["Learning Objective"] ?? [];
    allLearningObjectives = learningObjectives;
  }

  return allLearningObjectives;
}

export function sortLOsUsingName(a: LearningObjective, b: LearningObjective): number {
  const splitA = a.Name.split(".");
  const splitB = b.Name.split(".");

  const numA = parseInt(splitA[1], 10) ?? 0;
  const numB = parseInt(splitB[1], 10) ?? 0;
  const sectionA = splitA[2] ?? "";
  const sectionB = splitB[2] ?? "";

  if (numA < numB) return -1;
  if (numA > numB) return 1;
  if (numA === numB) {
    if (sectionA.localeCompare(sectionB) < 0) return -1;
    if (sectionA.localeCompare(sectionB) > 0) return 1;
  }

  return 0;
}

export function sortLOsUsingDisplayName(a: LearningObjective, b: LearningObjective): number {
  if (a["Display Name"] && b["Display Name"]) {
    const splitA = a["Display Name"].split(".");
    const splitB = b["Display Name"].split(".");

    if (splitA.length > 1 && splitB.length > 1) {
      const numA = parseInt(splitA[1], 10) ?? 0;
      const numB = parseInt(splitB[1], 10) ?? 0;

      if (isNaN(numA) || isNaN(numB)) {
        if (splitA[1].localeCompare(splitB[1]) < 0) return -1;
        if (splitA[1].localeCompare(splitB[1]) > 0) return 1;

        return 0;
      } else {
        if (numA < numB) return -1;
        if (numA > numB) return 1;

        return 0;
      }
    } else if (splitA.length === 1 && splitB.length === 1) {
      const numA = parseInt(splitA[0], 10) ?? 0;
      const numB = parseInt(splitB[0], 10) ?? 0;

      if (isNaN(numA) || isNaN(numB)) {
        if (splitA[0].localeCompare(splitB[0]) < 0) return -1;
        if (splitA[0].localeCompare(splitB[0]) > 0) return 1;

        return 0;
      } else {
        if (numA < numB) return -1;
        if (numA > numB) return 1;

        return 0;
      }
    }
  }

  return 0;
}

export function getAllLearningObjectivesForProgramByGroup(
  program: ProgramData
): Partial<Record<string, LearningObjective[]>> {
  const allLearningObjectives: LearningObjective[] = [];

  const progressChecks = program.ProgressChecks ?? [];
  const ids = new Set<string>();

  for (const progressCheck of progressChecks) {
    for (const competence of progressCheck["Competence/Activity"]) {
      for (const learningObjective of competence["Learning Objective"]!) {
        if (!ids.has(learningObjective.ID)) {
          allLearningObjectives.push(learningObjective);
          ids.add(learningObjective.ID);
        }
      }
    }
  }

  if (program.ID === PROGRAM_IDS.SWAST_SP_AP_RP) {
    allLearningObjectives.sort(sortLOsUsingDisplayName);
  } else {
    allLearningObjectives.sort(sortLOsUsingName);
  }

  const learningObjectives = _.groupBy(allLearningObjectives, (item) => item.TopSection!);

  const sortedKeys = Object.keys(learningObjectives).sort();

  const sortedLearningObjectives: any = {};

  sortedKeys.forEach((key) => {
    sortedLearningObjectives[key] = learningObjectives[key];
  });

  return sortedLearningObjectives;
}

export function getAllLearningObjectivesForRoleByGroup(
  program: ProgramData,
  roleName: string
): Partial<Record<string, LearningObjective[]>> {
  const allLearningObjectives = getAllLearningObjectivesForRole(program, roleName);

  const groupedLearningObjectives = _.groupBy(allLearningObjectives, (item) => item.TopSection!);

  let sortedKeys: string[] = [];

  sortedKeys = Object.keys(groupedLearningObjectives).sort();

  if (program.ID === PROGRAM_IDS.SWAST_SP_AP_RP) {
    // Swap Leadership and Management with Education
    sortedKeys = Object.keys(groupedLearningObjectives).sort((a, b) => {
      if (a.toLowerCase().includes("education")) {
        return 1;
      }
      return a.localeCompare(b);
    });
  } else if (program.ID === PROGRAM_IDS.NWAS_ARC) {
    // Swap Leadership and Management with Education
    sortedKeys = Object.keys(groupedLearningObjectives).sort((a, b) => {
      if (a.toLowerCase().includes("education")) {
        return 1;
      }
      return a.localeCompare(b);
    });
  }

  const sortedLearningObjectives: any = {};

  sortedKeys.forEach((key) => {
    sortedLearningObjectives[key] = groupedLearningObjectives[key];
  });

  return sortedLearningObjectives;
}

/**
 * Calculates the user's NOS compliance
 * @param standards - A concatenated array of the Mandatory Standards for the user's role and their selected Optional Standards
 * @param programsData - The Program Module data
 * @param user - The current user object
 * @param programEvidence - The Program Module evidence
 * @returns The compliance percentage
 */
export function getCompliantCountForStandards(
  standards: IOptionalStandard[] | IMandatoryStandard[],
  programsData: any,
  user: IUser | null,
  programEvidence: IEvidence[],
  programId: string
) {
  if (!user || !programEvidence) return 0;
  // TODO: Implement for Test Program
  if (programId === PROGRAM_IDS.TEST_PROGRAM) return 0;

  let count = 0;
  standards.forEach((standard) => {
    const eprr: any = programsData.find((program: any) => program.ID === programId);
    const pm = new ProgramModule(eprr);
    const contactID = Number(user.userData.contactID);
    const roleID = getSelectedRoleIdForEPRRProgram(user, programId);
    const pMUsageData = new PMUsageData(eprr, []);
    const filteredEvidence = programEvidence
      .filter((ev) => !ev.draft)
      .filter((ev) => new Date(ev.date) >= subYears(new Date(), 2)) as [];
    const results = pMUsageData.getUsageDataByUser(pm, contactID, roleID, filteredEvidence);
    const resultForCurrentStandard = results.find((result: any) => result.compId === standard.id);

    if (resultForCurrentStandard) {
      const percentage = Math.floor(Number(resultForCurrentStandard.percentage));
      const isCompliant = isSwastEPRR(programId) ? isSwastEprrNosCompliant(percentage) : percentage >= 90;

      if (isCompliant) {
        count += 1;
      }
    }
  });

  return count;
}

/**
 * @param evidence - All of the user's evidence
 * @param field - The key inside the evidenceJSON object that contains the graph label value
 * @returns
 */
export function getConfidenceGraphLabel(evidence: IEvidence[], field: string, programID?: string): string | undefined {
  const evidenceWithConfidenceLevels = evidence.find((evidence: IEvidence) => {
    const evidenceJSON = evidence.evidenceJSON;
    const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID;

    const onHoldReset = evidenceJSON?.OnHoldReset;

    const evidenceProgramID = evidenceJSON?.programInfo?.programID;

    if (programID && evidenceProgramID !== programID) {
      return false;
    }

    let confidenceLevelCompetenceID = "";

    if (evidenceProgramID === PROGRAM_IDS.NEAS_NQP) {
      // NEAS NQP
      confidenceLevelCompetenceID = NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.NEAS_NQP_0_6_MONTHS;
    } else if (evidenceProgramID === PROGRAM_IDS.SWAST_PRECEPTORSHIP) {
      // SWASFT Preceptorship
      confidenceLevelCompetenceID = NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.SWAST_PRECEPTORSHIP_PRE;
    } else if (evidenceProgramID === PROGRAM_IDS.YAS_NQP) {
      // YAS NQP
      confidenceLevelCompetenceID = NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.YAS_NQP_0_6_MONTHS;
    } else if (evidenceProgramID === PROGRAM_IDS.LAS_NQP) {
      // LAS NQP
      confidenceLevelCompetenceID = NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.LAS_NQP_6_MONTH_REVIEW;
    }

    const value =
      progressCheckCompetenceID === confidenceLevelCompetenceID && onHoldReset !== 1 && evidence.draft !== true;

    return value;
  });

  if (!evidenceWithConfidenceLevels) {
    return undefined;
  }
  const evidenceJSON = evidenceWithConfidenceLevels.evidenceJSON;

  return evidenceJSON[field];
}

// TODO: Should get initial value from first evidence for confidence levels in YAS NQP
export function getConfidenceLevelTextBoxInitialValue(evidence: IEvidence[], field: string): void {
  const evidenceWithConfidenceLevels = evidence.find((evidence: IEvidence) => {
    const evidenceJSON = evidence.evidenceJSON;
    const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID;

    const onHoldReset = evidenceJSON?.OnHoldReset;

    const programID = evidenceJSON?.programInfo?.programID;

    let confidenceLevelCompetenceID = "";

    if (programID === PROGRAM_IDS.YAS_NQP) {
      // YAS NQP
      confidenceLevelCompetenceID = NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.YAS_NQP_0_6_MONTHS;
    }

    const value =
      progressCheckCompetenceID === confidenceLevelCompetenceID && onHoldReset !== 1 && evidence.draft !== true;

    return value;
  });

  if (!evidenceWithConfidenceLevels) {
    return undefined;
  }
  const evidenceJSON = evidenceWithConfidenceLevels.evidenceJSON;

  return evidenceJSON[field];
}

/**
 * Retrieves all the evidence required to create the confidence level graph
 * @param programEvidence - All the evidence for the current program
 * @returns
 */
export function getConfidenceLevelEvidence(programEvidence: IEvidence[], program: string) {
  let evidenceWithConfidenceLevels: IEvidence[] = [];

  evidenceWithConfidenceLevels = programEvidence.filter((evidence: IEvidence) => {
    const evidenceJSON = evidence.evidenceJSON;
    const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID;
    const programID = evidenceJSON?.programInfo?.programID;

    return competenceIsConfidenceLevel(progressCheckCompetenceID) && programID === program;
  });

  return evidenceWithConfidenceLevels;
}

/**
 * @param progressCheckID - The ID of the progress check
 * @returns The index to insert the confidence level data into
 */
export function getConfidenceLevelProgressCheckIndex(progressCheckID: string): number {
  switch (progressCheckID) {
    case NQP_CONFIDENCE_LEVEL_PROGRESS_CHECK_IDS.SWAST_PRECEPTORSHIP_START_POINT:
    case NQP_CONFIDENCE_LEVEL_PROGRESS_CHECK_IDS.NEAS_NQP_0_6_MONTHS:
    case NQP_CONFIDENCE_LEVEL_PROGRESS_CHECK_IDS.YAS_NQP_0_6_MONTHS:
    case NQP_CONFIDENCE_LEVEL_PROGRESS_CHECK_IDS.LAS_NQP_6_MONTH_REVIEW:
      return 0;
    case NQP_CONFIDENCE_LEVEL_PROGRESS_CHECK_IDS.SWAST_PRECEPTORSHIP_7_SHIFTS:
    case NQP_CONFIDENCE_LEVEL_PROGRESS_CHECK_IDS.NEAS_NQP_6_12_MONTHS:
    case NQP_CONFIDENCE_LEVEL_PROGRESS_CHECK_IDS.YAS_NQP_6_12_MONTHS:
    case NQP_CONFIDENCE_LEVEL_PROGRESS_CHECK_IDS.LAS_NQP_12_MONTHS_REVIEW:
      return 1;
    case NQP_CONFIDENCE_LEVEL_PROGRESS_CHECK_IDS.SWAST_PRECEPTORSHIP_14_SHIFTS:
    case NQP_CONFIDENCE_LEVEL_PROGRESS_CHECK_IDS.NEAS_NQP_12_18_MONTHS:
    case NQP_CONFIDENCE_LEVEL_PROGRESS_CHECK_IDS.YAS_NQP_12_18_MONTHS:
    case NQP_CONFIDENCE_LEVEL_PROGRESS_CHECK_IDS.LAS_NQP_18_MONTHS_REVIEW:
      return 2;
    case NQP_CONFIDENCE_LEVEL_PROGRESS_CHECK_IDS.SWAST_PRECEPTORSHIP_END_OF_PRECEPTORSHIP:
    case NQP_CONFIDENCE_LEVEL_PROGRESS_CHECK_IDS.NEAS_NQP_18_24_MONTHS:
    case NQP_CONFIDENCE_LEVEL_PROGRESS_CHECK_IDS.YAS_NQP_18_24_MONTHS:
    case NQP_CONFIDENCE_LEVEL_PROGRESS_CHECK_IDS.LAS_NQP_24_MONTHS_REVIEW:
      return 3;
    default:
      return -1;
  }
}

/**
 * Gets the user's selected area (region)
 * @param programRoles - The user's Program Roles array
 * @returns The area if it exists, otherwise returns an empty string
 */
export function getEprrArea(programRoles: IUserProgramRoles[], programId: string) {
  if (!programRoles || programRoles.length === 0) return "";

  const eprrProgram = programRoles.find((program) => program.programID === programId);
  return eprrProgram ? eprrProgram.area : "";
}

export function getProgramLevel(programRoles: IUserProgramRoles[], programId: string) {
  if (!programRoles || programRoles.length === 0) return "";

  const programRole = programRoles.find((program) => program.programID === programId);
  return programRole ? programRole.level : "";
}

export function getNQPArea(programRoles: IUserProgramRoles[], programId: string) {
  if (!programRoles || programRoles.length === 0) return "";

  const nqpProgram = programRoles.find((program) => program.programID === programId);
  return nqpProgram ? nqpProgram.area : "";
}

/**
 * Gets the key of the evidence part that contains the activity type
 * @param evidence - The current piece of evidence
 * @returns The evidence parts key that contains the activity type
 */
export function getEvidenceActivityTypeKey(evidence: IEvidence): string | undefined {
  let evidenceJSON;

  if (typeof evidence.evidenceJSON === "string") {
    JSON.parse(evidence.evidenceJSON);
  } else if (typeof evidence.evidenceJSON === "object") {
    evidenceJSON = evidence.evidenceJSON;
  }

  if (evidenceJSON) {
    switch (evidenceJSON.programInfo.program) {
      case "SWAST HART and EPRR Instructors":
        return "CPD Activity - HART and EPRR";
      default:
        return;
    }
  }

  return;
}

/**
 * Gets the key of the attachments upload evidence part from an array of evidence parts
 * @param evidenceParts - The evidence parts to check
 * @returns The key of the attachments upload evidence part
 */
export function getEvidenceAttachmentsKey(evidenceParts: EvidencePart[]): string | undefined {
  const attachmentsPart = evidenceParts.find((item) => item["Field type"] === FieldType.Attachments);

  if (attachmentsPart) {
    const key = attachmentsPart.Name;

    return key;
  }

  return;
}

/**
 * Gets all keys of attachments upload evidence parts from an array of evidence parts
 * @param evidenceParts - The evidence parts to check
 * @returns An array of keys for all attachment upload evidence parts
 */
export function getEvidenceAttachmentsKeys(evidenceParts: EvidencePart[]): string[] {
  const flattenedParts = EvidenceUtils.flattenEvidenceParts(evidenceParts);

  return flattenedParts.filter((item) => item["Field type"] === FieldType.Attachments).map((item) => item.Name);
}

/**
 * @param programEvidence - The evidence for the current program
 * @param progressCheckID  - The ID of the progress check
 * @returns The number of pieces of evidence the user has created for the current progress check
 */
export function getEvidenceCountForProgressCheck(programEvidence: IEvidence[], progressCheckID: string): number {
  const progressCheckEvidence = programEvidence.filter((evidence) => {
    const evidenceJSON = evidence.evidenceJSON;
    const id = evidenceJSON?.programInfo?.progressCheckID || "";

    const onHoldReset = evidenceJSON.OnHoldReset;

    const value = id === progressCheckID && onHoldReset !== 1 && evidence.draft !== true;

    return value;
  });

  return progressCheckEvidence.length;
}

/**
 * Gets the key of the evidence part that contains the custom tags
 * @param evidence - The current piece of evidence
 * @returns The evidence parts key that contains the custom tags
 */
export function getEvidenceCustomTagsKey(evidence: IEvidence): string | undefined {
  let evidenceJSON;

  if (typeof evidence.evidenceJSON === "string") {
    JSON.parse(evidence.evidenceJSON);
  } else if (typeof evidence.evidenceJSON === "object") {
    evidenceJSON = evidence.evidenceJSON;
  }

  if (evidenceJSON) {
    switch (evidenceJSON.programInfo.program) {
      case "SWAST HART and EPRR Instructors":
        return "Custom tags";
      default:
        return;
    }
  }

  return;
}

/**
 * @param submissionState - The ProgressCheckSubmission object for the current progress check
 * @returns The correct disclaimer text if the submission status is Submitted or Approved, otherwise an empty string
 */
export function getEvidenceDisclaimerText(
  submissionState: ProgressCheckSubmission | undefined,
  programID?: string
): string {
  if (programID === PROGRAM_IDS.UOC_APPRENTICESHIP) {
    switch (submissionState?.status) {
      case SubmissionState.Submitted:
        return "You can no longer add or edit evidence to this year because it is waiting for academic approval.";
      case SubmissionState.Approved:
        return "You can no longer add new evidence or edit existing evidence because this year has been approved.";
      default:
        return "You can no longer add new evidence or edit existing evidence in this year.";
    }
  }

  switch (submissionState?.status) {
    case SubmissionState.Submitted:
      return "You can no longer add or edit evidence to this progress check now that you have submitted it for approval.";
    case SubmissionState.Approved:
      return "You can no longer add new evidence or edit existing evidence because this progress check is approved.";
    case SubmissionState.Completed:
      return "You can no longer add new evidence or edit existing evidence because this progress check is completed.";
    default:
      return "You can no longer add new evidence or edit existing evidence in this progress check.";
  }
}

/**
 * Retrieves all of the user's evidence for the current program
 * @param allEvidence - All of the current user's evidence
 * @param program - The current program name
 * @returns The filtered evidence for the program
 */
export function getEvidenceForProgramName(allEvidence: IEvidence[], programName: string) {
  let evidenceForProgram: IEvidence[] = [];

  evidenceForProgram = allEvidence?.filter((evidence: IEvidence) => {
    const evidenceJSON = evidence.evidenceJSON;
    const program = evidenceJSON?.programInfo?.program || "";

    return program === programName;
  });

  evidenceForProgram = _.orderBy(evidenceForProgram, "date", "desc");

  return evidenceForProgram;
}

/**
 * Retrieves all of the user's Evidence for the current Program Module
 * @param allEvidence - All of the current user's Evidence
 * @param program - The current Program Module's name
 * @returns The filtered Evidence for the Program Module
 */
export function getEvidenceForProgramID(allEvidence: IEvidence[], programID: string) {
  let evidenceForProgram: IEvidence[] = [];

  evidenceForProgram = allEvidence?.filter((evidence: IEvidence) => {
    const evidenceJSON = evidence.evidenceJSON;
    const id = evidenceJSON?.programInfo?.programID || "";

    return id === programID;
  });

  evidenceForProgram = _.orderBy(evidenceForProgram, "date", "desc");

  return evidenceForProgram;
}

/**
 * Retrieves all of the user's Evidence for the current Progress Check
 * @param programEvidence - All of the user's Evidence for the current Progress Check
 * @param progressCheckID - The current Progress Check ID
 * @returns The filtered Evidence for the Progress Check
 */
export function getEvidenceForProgressCheck(programEvidence: IEvidence[] = [], progressCheckID: string): IEvidence[] {
  let result: IEvidence[] = [];
  result = programEvidence?.filter((evidence: IEvidence) => {
    const evidenceJSON = evidence.evidenceJSON;
    const id = evidenceJSON?.programInfo?.progressCheckID || "";

    const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

    return id === progressCheckID && onHoldReset !== 1 && evidence.draft !== true;
  });

  return result;
}

/**
 * @param evidencePart - The Evidence Part to get the value for
 * @param evidenceJSON
 * @param evidence - All of the user's Evidence
 * @param progressCheckCompetence - The current Progress Check Competence if needed (for ConfidenceLevelGraph)
 * @returns The initial value for the Evidence Part
 */
export function getEvidencePartInitialValue(
  evidencePart: EvidencePart,
  evidenceJSON: any,
  evidence: IEvidence[],
  progressCheckCompetence?: CompetenceActivityClass | null,
  programID?: string
): any {
  if (evidencePart["Field type"] === FieldType.NOSSelect) {
    return {
      Comps: evidenceJSON.Comps ?? [],
      LOs: evidenceJSON.LOs ?? [],
    };
  } else if (evidencePart["Field type"] === FieldType.CompMultiSelect) {
    return evidenceJSON["Comp Selector"];
  } else if (evidencePart["Field type"] === FieldType.LearningOutcomesList) {
    return evidenceJSON["LearningOutcomes"];
  } else if (isFieldLocked(progressCheckCompetence?.ID, evidencePart.ID)) {
    return getConfidenceGraphLabel(evidence, evidencePart.Name, programID);
  } else if (shouldGetInitialValueFromEvidence(progressCheckCompetence?.ID, evidencePart.ID)) {
    if (evidenceJSON[evidencePart.Name]) {
      return evidenceJSON[evidencePart.Name];
    } else {
      return getConfidenceLevelTextBoxInitialValue(evidence, evidencePart.Name);
    }
  } else {
    return evidenceJSON[evidencePart.Name];
  }
}

export function shouldGetInitialValueFromEvidence(
  progressCheckCompetenceID: string | undefined,
  evidencePartID: string
): boolean {
  if (!progressCheckCompetenceID) {
    return false;
  }

  const competenceIncluded = [
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.YAS_NQP_6_12_MONTHS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.YAS_NQP_12_18_MONTHS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.YAS_NQP_18_24_MONTHS,
  ].includes(progressCheckCompetenceID);

  const partShouldGetInitialValue = [PROGRAM_EVIDENCE_PART_IDS.YAS_NQP_CONFIDENCE_LEVEL_TEXT_BOX].includes(
    evidencePartID
  );

  return competenceIncluded && partShouldGetInitialValue;
}

/**
 *
 * @param mandatoryStandards - All of the Mandatory Standards for the user's chosen Role
 * @param optionalStandards - The Optional Standards that the user has selected
 * @param selectedStandard - The Standard that the user has selected in the Evidence Modal
 * @param learningObjectives - All of the Learning Objective for the Program Module
 * @returns The user's selected Learning Objectives that correspond to the selected Standard
 */
export function getLearningObjectivesForStandard(
  mandatoryStandards: CompetenceActivityClass[] | undefined,
  optionalStandards: CompetenceActivityClass[] | undefined,
  selectedStandard: INOSSelectCompetence,
  learningObjectives: INOSSelectLearningObjectives[]
): INOSSelectLearningObjectives[] | undefined {
  const standards = mandatoryStandards?.concat(optionalStandards ?? []) ?? [];
  const standard = standards?.find((item) => item.ID === selectedStandard.id);
  const mappedLearningObjectives = standard?.["Learning Objective"]?.map((item) => item.ID) ?? [];

  const array = learningObjectives.filter((item) => mappedLearningObjectives.includes(item.id));

  return array ?? undefined;
}

/**
 *
 * @param user - Current user object
 * @param program - The current Program Module
 * @returns Mandatory standards for the user's primary role, plus any additional mandatory standards from selected secondary roles (EPRR only)
 */
export function getMandatoryStandards(user: IUser, program: ProgramData): CompetenceActivityClass[] {
  if (!user || !program) {
    return [];
  }

  const eprrProgram = user.programRoles?.find((role) => role.programID === program.ID);

  if (!eprrProgram?.programRoles) {
    return [];
  }

  const selectedRoleID = eprrProgram?.programRoles[0]?.roleID;

  if (!selectedRoleID) {
    return [];
  }

  const mandatoryStandards = [...getMandatoryCompetenceActivityForRole(program, selectedRoleID)];
  const includedStandardIds = new Set(mandatoryStandards.map((standard) => standard.ID));

  if (!showSecondaryRoleStandardsInProgramHomePage(program.ID)) {
    return mandatoryStandards;
  }

  const primaryStandardIds = getPrimaryRoleStandardIds(user, program);

  getSelectedSecondaryRolesForEPRRProgram(user, program.ID).forEach((secondaryRole) => {
    const roleId = secondaryRole.roleID || findSecondaryRoleIdByName(program, selectedRoleID, secondaryRole.roleName);

    getMandatoryCompetenceActivityForRole(program, roleId)
      .filter((standard) => !primaryStandardIds.has(standard.ID))
      .forEach((standard) => {
        if (!includedStandardIds.has(standard.ID)) {
          includedStandardIds.add(standard.ID);
          mandatoryStandards.push(standard);
        }
      });
  });

  return mandatoryStandards;
}

export function showPrimaryLabelInProgramRolesDisplay(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_EPRR, PROGRAM_IDS.SWAST_EPRR].includes(programID);
}

/**
 * Checks whether optional standards should be shown as a separate section on the program home page. Only applies to NWAS and SWAST EPRR because they have secondary roles.
 * @param programID - The current program ID
 * @returns
 */
export function showOptionalStandardsAsSeparateSection(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_EPRR, PROGRAM_IDS.SWAST_EPRR].includes(programID);
}

function getMandatoryCompetenceActivityForRole(program: ProgramData, roleId: string): CompetenceActivityClass[] {
  if (!roleId) {
    return [];
  }

  const role =
    program.Roles?.find((item) => item.ID === roleId) ??
    program.Roles?.flatMap((item) => [
      ...(item.MandatorySecondaryRoles ?? []),
      ...(item.OptionalSecondaryRoles ?? []),
    ]).find((item) => item.ID === roleId);

  if (role?.CompetenceActivity?.length && checkCompetenceActivityType("Name", role.CompetenceActivity[0])) {
    return [...(role.CompetenceActivity as CompetenceActivityClass[])];
  }

  return [];
}

/**
 * Retrieves the mandatory standards for a selected role
 * @param {any} program - the whole program json
 * @param {string} roleId - role id to obtain standards for
 * @returns An array of mandatory standards
 */
export function getMandatoryStandardsFromProgram(program: any, roleId: string): IMandatoryStandard[] {
  if (!roleId) {
    return [];
  }

  const role = program.Roles.find((r: any) => r.ID === roleId);
  const competenceActivity = role?.CompetenceActivity ?? role?.competenceActivity;

  if (!Array.isArray(competenceActivity)) {
    return [];
  }

  const mandatoryStandards: IMandatoryStandard[] = competenceActivity.map((item: any) => {
    const learningObjectives: LearningObjective[] = Array.from(item["Learning Objective"] ?? []).map((lo: any) => lo);
    return { id: item.ID, name: item.Name, description: item.Description, learningObjectives };
  });

  return mandatoryStandards;
}

/**
 * Retrieves the optional standards defined for a role
 */
export function getOptionalStandardsFromProgramRole(program: any, roleId: string): IMandatoryStandard[] {
  if (!roleId) {
    return [];
  }

  const role = program.Roles.find((r: any) => r.ID === roleId);
  const optionalCompetenceActivity = role?.OptionalCompetenceActivity;

  if (!Array.isArray(optionalCompetenceActivity) || !optionalCompetenceActivity.length) {
    return [];
  }

  if (!checkCompetenceActivityType("Name", optionalCompetenceActivity[0])) {
    return [];
  }

  return (optionalCompetenceActivity as CompetenceActivityClass[]).map((item) => {
    const learningObjectives: LearningObjective[] = Array.from(item["Learning Objective"] ?? []).map((lo) => lo);
    return { id: item.ID, name: item.Name, description: item.Description || "", learningObjectives };
  });
}

/**
 * Finds a nested secondary role ID by name under the selected primary role
 */
export function findSecondaryRoleIdByName(program: ProgramData, primaryRoleId: string, roleName: string): string {
  const primaryRole = program.Roles?.find((role) => role.ID === primaryRoleId);
  const secondaryRoles = [
    ...(primaryRole?.MandatorySecondaryRoles ?? []),
    ...(primaryRole?.OptionalSecondaryRoles ?? []),
  ];

  return secondaryRoles.find((role) => role.Name === roleName)?.ID ?? "";
}

/**
 * Returns selected secondary roles for an EPRR program
 */
export function getSelectedSecondaryRolesForEPRRProgram(
  user: IUser | null,
  programId: string
): { roleID: string; roleName: string }[] {
  if (!user) {
    return [];
  }

  const eprrProgram = user.programRoles?.find((role) => role.programID === programId);
  if (!eprrProgram?.secondaryRoles?.length) {
    return [];
  }

  return eprrProgram.secondaryRoles.map((item: any) =>
    typeof item === "string" ? { roleID: "", roleName: item } : { roleID: item.roleID, roleName: item.roleName }
  );
}

/**
 * Returns secondary role names for a program
 */
export function getSecondaryRoleNamesForProgram(
  user: IUser | null | undefined,
  programName: string,
  programId: string
): string[] {
  if (!user?.programRoles) {
    return [];
  }

  const programEntry =
    user.programRoles.find((role) => role.programName === programName) ??
    user.programRoles.find((role) => role.programID === programId);

  if (!programEntry?.secondaryRoles?.length) {
    return [];
  }

  return programEntry.secondaryRoles.map((item: any) => (typeof item === "string" ? item : item.roleName));
}

/**
 * Returns standard IDs shown for the primary role (mandatory from program + user-selected optional).
 */
export function getPrimaryRoleStandardIds(user: IUser, program: ProgramData): Set<string> {
  const primaryRoleId = getSelectedRoleIdForEPRRProgram(user, program.ID);

  return new Set([
    ...getMandatoryStandardsFromProgram(program, primaryRoleId).map((standard) => standard.id),
    ...getOptionalStandards(user, program).map((standard) => standard.ID),
  ]);
}

/**
 * Returns mandatory and optional standards for a secondary role from program data,
 * excluding any standards already assigned to the primary role.
 */
export function getSecondaryRoleStandardsExcludingPrimary(
  program: ProgramData,
  secondaryRoleId: string,
  primaryStandardIds: Set<string>
): { mandatory: IMandatoryStandard[]; optional: IMandatoryStandard[] } {
  return {
    mandatory: getMandatoryStandardsFromProgram(program, secondaryRoleId).filter(
      (standard) => !primaryStandardIds.has(standard.id)
    ),
    optional: getOptionalStandardsFromProgramRole(program, secondaryRoleId).filter(
      (standard) => !primaryStandardIds.has(standard.id)
    ),
  };
}

/**
 * Returns all mandatory standards for an EPRR program, including secondary roles
 */
export function getAllMandatoryStandardsForEPRRProgram(user: IUser | null, program: ProgramData): IMandatoryStandard[] {
  if (!user) {
    return [];
  }

  const primaryRoleId = getSelectedRoleIdForEPRRProgram(user, program.ID);
  const mandatoryStandards = getMandatoryStandardsFromProgram(program, primaryRoleId);
  const includedStandardIds = new Set(mandatoryStandards.map((standard) => standard.id));

  if (!showSecondaryRoleStandardsInProgramHomePage(program.ID)) {
    return mandatoryStandards;
  }

  const primaryStandardIds = getPrimaryRoleStandardIds(user, program);

  getSelectedSecondaryRolesForEPRRProgram(user, program.ID).forEach((secondaryRole) => {
    const roleId = secondaryRole.roleID || findSecondaryRoleIdByName(program, primaryRoleId, secondaryRole.roleName);

    getSecondaryRoleStandardsExcludingPrimary(program, roleId, primaryStandardIds).mandatory.forEach((standard) => {
      if (!includedStandardIds.has(standard.id)) {
        includedStandardIds.add(standard.id);
        mandatoryStandards.push(standard);
      }
    });
  });

  return mandatoryStandards;
}

/**
 * Returns all optional standards for an EPRR program, including secondary roles
 */
export function getAllOptionalStandardsForEPRRProgram(user: IUser | null, program: ProgramData): IOptionalStandard[] {
  if (!user) {
    return [];
  }

  const primaryRoleId = getSelectedRoleIdForEPRRProgram(user, program.ID);
  const optionalStandards = getOptionalStandardsFromUser(user, program);
  const includedStandardIds = new Set(optionalStandards.map((standard) => standard.id));

  if (!showSecondaryRoleStandardsInProgramHomePage(program.ID)) {
    return optionalStandards;
  }

  const primaryStandardIds = getPrimaryRoleStandardIds(user, program);

  getSelectedSecondaryRolesForEPRRProgram(user, program.ID).forEach((secondaryRole) => {
    const roleId = secondaryRole.roleID || findSecondaryRoleIdByName(program, primaryRoleId, secondaryRole.roleName);

    getSecondaryRoleStandardsExcludingPrimary(program, roleId, primaryStandardIds).optional.forEach((standard) => {
      if (!includedStandardIds.has(standard.id)) {
        includedStandardIds.add(standard.id);
        optionalStandards.push(standard);
      }
    });
  });

  return optionalStandards;
}

/**
 * Checks whether secondary role standards should be shown on the program home page
 */
export function showSecondaryRoleStandardsInProgramHomePage(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_EPRR, PROGRAM_IDS.SWAST_EPRR].includes(programID);
}

/**
 * Returns the standard tags for the user's SWAST IC role
 * @param role - The current user's SWAST IC role
 * @returns The mapped array of mandatory and optional standard tags
 */
export function getNOSStandardTagsForRole(role: string) {
  const roleObject = roles.find((item) => item.Name === role);

  const optional = roleObject?.OptionalRoles;
  const mandatory = roleObject?.MandatoryRoles;

  const mandatoryRoles = mandatory?.map((id) => standards.find((item) => item.id === id));
  const optionalRoles = optional?.map((id) => standards.find((item) => item.id === id));

  return {
    mandatory: _.orderBy(mandatoryRoles, ["Code"], ["asc"]) || [],
    optional: _.orderBy(optionalRoles, ["Code"], ["asc"]) || [],
  };
}

/**
 *
 * @param user - Current user object
 * @param program - The current Program Module
 * @returns An array of the Optional Standards for the user's selected role if selected, otherwise an empty array
 */
export function getOptionalStandards(user: IUser, program: ProgramData): CompetenceActivityClass[] {
  if (!user || !program) {
    return [];
  }

  const eprrProgram = user.programRoles?.find((role) => role.programID === program.ID);

  if (!eprrProgram?.programRoles) {
    return [];
  }

  const selectedRoleID = eprrProgram?.programRoles[0] && eprrProgram?.programRoles[0].roleID;

  if (!selectedRoleID) {
    return [];
  }

  const optionalStandards = (eprrProgram?.programRoles[0] && eprrProgram?.programRoles[0].optionalStandards) ?? [];
  const optionalStandardsIDs = optionalStandards.map((item: any) => item.id);

  const role = program.Roles?.find((item) => item.ID === selectedRoleID);

  if (
    role?.OptionalCompetenceActivity?.length &&
    checkCompetenceActivityType("Name", role.OptionalCompetenceActivity[0])
  ) {
    const array: CompetenceActivityClass[] = role.OptionalCompetenceActivity as CompetenceActivityClass[];

    const selectedStandards = array.filter((item) => optionalStandardsIDs.includes(item.ID));

    return selectedStandards ?? [];
  }

  return [];
}

export function getSkillsForRole(user: IUser, program: ProgramData): ProgramSkill[] {
  if (user && program) {
    const programRoles = user.programRoles?.find((role) => role.programName === program.Name);
    const roles = programRoles?.programRoles;

    if (roles) {
      const skillsForRole =
        (program && (program as ProgramData)?.Roles?.find((role) => role.Name === roles[0])?.Skills) ?? [];

      const sortedSkillsForRole = _.sortBy(skillsForRole, "Name");

      return sortedSkillsForRole;
    }
  }

  return [];
}

/**
 * Returns optional standards for the primary role: program OptionalCompetenceActivity
 * filtered to those the user has selected (see getOptionalStandards).
 */
export function getOptionalStandardsFromUser(user: IUser | null, program: ProgramData): IMandatoryStandard[] {
  if (!user) {
    return [];
  }

  return getOptionalStandards(user, program).map((item) => ({
    id: item.ID,
    name: item.Name,
    description: item.Description || "",
    learningObjectives: Array.from(item["Learning Objective"] ?? []),
  }));
}

export function getPauseLabel(programModuleStatus: ProgramModuleStatus | undefined): PauseLabel | undefined {
  switch (programModuleStatus?.data.Pause) {
    case "Pause":
      return {
        label: "Pause",
        status: "Paused",
      };
    case "PauseAndHold":
      return {
        label: "Pause & hold",
        status: "Pause & Hold",
      };
    case "Extension":
      return {
        label: "Extension",
        status: "Extension",
      };
    default:
      return undefined;
  }
}

export function getPauseDescription(
  programModuleStatus: ProgramModuleStatus | undefined,
  program?: ProgramData
): string | undefined {
  switch (programModuleStatus?.data.Pause) {
    case "Pause":
      return program?.PauseDescription;
    case "PauseAndHold":
      return program?.PauseAndHoldDescription;
    case "Extension":
      return program?.PauseAndExtensionDescription;
    default:
      return undefined;
  }
}

export function getFastTrackLabel(programModuleStatus: ProgramModuleStatus | undefined): FastTrackLabel | undefined {
  switch (programModuleStatus?.data.FastTrack) {
    case "Enabled":
      return {
        label: "Fast Track",
        status: "Fast Track",
      };
    default:
      return undefined;
  }
}

export function getFastTrackDescription(
  programModuleStatus: ProgramModuleStatus | undefined,
  program?: ProgramData
): string | undefined {
  switch (programModuleStatus?.data.FastTrack) {
    case "Enabled":
      return program?.FastTrackDescription;
    default:
      return undefined;
  }
}

export function getPreceptorsText(mentorNamingConvention?: string, mentorTitle?: string): string | undefined {
  if (mentorNamingConvention) {
    return `Your ${mentorNamingConvention}`;
  } else if (mentorTitle) {
    return `Your ${mentorTitle}${makePlural(mentorTitle)}`;
  }

  return undefined;
}

function makePlural(input: string): string {
  if (input[input.length - 1] !== "s") {
    return "s";
  }

  return "";
}

/**
 * Finds the user's ParaFolio subscriptions and maps any ParaFolioPrograms fields withing them to the corresponding programID
 * @param subscriptions - All of the users Class subscriptions, not only ParaFolio subscriptions
 * @param programs - The full list of programs
 * @returns An array of ProgramIDs
 */
export function getProgramIDsFromSubscriptions(
  subscriptions: IClassSubscription[] | null,
  programs: ProgramData | any
): string[] {
  if (subscriptions) {
    const programSubscriptions: IClassSubscription[] = subscriptions.filter(
      (item: IClassSubscription) => item?.ParaFolioPrograms && item?.ParaFolioPrograms.length > 0
    );

    const subscriptionNameArrays = programSubscriptions.map((item) => item.ParaFolioPrograms);
    const uniqueProgramName = _.uniq(_.flatten(subscriptionNameArrays));

    let programIDs = uniqueProgramName.map((item) => {
      const program = programs.find((_item: any): _item is ProgramData => {
        return _item.Subscription?.includes(item) ?? undefined;
      });

      return program?.ID ?? undefined;
    });

    programIDs = programIDs.filter((item) => item !== undefined);

    return programIDs;
  }

  return [];
}

/**
 *
 * @param programName - The current Program Module's name
 * @returns The CSS class name for the ProgramCard background style
 */
export function getProgramCardBackground(programName: string): string {
  return "programCardBackground" + programName.replace(/\s/gim, "").replace(/:/, "");
}

/**
 * Returns the progress check for the current piece of evidence based on the progressCheckID
 * @param evidenceJSON
 * @param program
 * @returns The progress check if it exists or null if it doesn't
 */
export function getProgressCheckForEvidence(evidenceJSON: any, program: ProgramData | undefined): ProgressCheck | null {
  if (program) {
    const progressCheckID = evidenceJSON?.programInfo?.progressCheckID;
    const progressChecks = program.ProgressChecks || [];

    const progressCheck = progressChecks.find((item: ProgressCheck) => item.ID === progressCheckID);

    return progressCheck ? progressCheck : null;
  }

  return null;
}

export function getProgressCheckCompetenceActivityForEvidence(
  evidenceJSON: any,
  program: ProgramData | undefined
): CompetenceActivityClass | null {
  if (program) {
    const progressCheckID = evidenceJSON?.programInfo?.progressCheckID;
    const progressChecks = program.ProgressChecks || [];

    const progressCheck = progressChecks.find((item: ProgressCheck) => item.ID === progressCheckID);

    if (progressCheck) {
      const progressCheckCompetence = progressCheck["Competence/Activity"].find(
        (item: CompetenceActivityClass) => item.ID === evidenceJSON?.programInfo?.progressCheckCompetenceID
      );

      return progressCheckCompetence ?? null;
    }
  }

  return null;
}

export function getProgressCheckSubmissionLockedDisclaimerText(
  progressCheckData: ProgressCheckStatus | null
): string | undefined {
  switch (progressCheckData?.submissions.status) {
    case SubmissionState.Submitted:
      return "You can no longer add or edit evidence to this progress check now that you have submitted it for approval.";
    case SubmissionState.Approved:
      return "You can no longer add new evidence or edit existing evidence because this progress check is approved.";
    case SubmissionState.Completed:
      return "You can no longer add new evidence or edit existing evidence because this progress check is completed.";
    case SubmissionState.Rejected:
      return "You can no longer add new evidence or edit existing evidence because this progress check has been rejected.";
    default:
      return undefined;
  }
}

/**
 *
 * @param progressCheckID
 * @returns The correct info string whether the previous Progress Check required approval or not
 */
export function getProgressCheckLockedDisclaimerText(progressCheckID: string): string {
  switch (progressCheckID) {
    case PROGRESS_CHECK_IDS.SWAST_PRECEPTORSHIP_BAND_6_REFLECTIONS:
    case NQP_CONFIDENCE_LEVEL_PROGRESS_CHECK_IDS.SWAST_PRECEPTORSHIP_END_OF_PRECEPTORSHIP:
    case PROGRESS_CHECK_IDS.NEAS_NQP_INDUCTION:
      return "You cannot add evidence to this progress check until you have completed the previous progress check.";
    default:
      return "You cannot add evidence to this progress check until your previous progress check has been approved.";
  }
}

export function getProgressCheckPauseAndHoldLockedText(pauseAndHold: boolean, isProgram?: boolean): string | undefined {
  if (pauseAndHold) {
    return `You cannot add evidence${isProgram ? " " : " to this progress check "}because your program has been paused.`;
  }
}

/**
 *
 * @param progressCheckID - The ID of the Progress Check to find
 * @param progressCheckData - The array of all Progress Check Status data
 * @param program
 * @returns The Progress Check Status if it exists or null if it doesn't
 */
export function getProgressCheckStatusFromEvidence(
  progressCheckID: string,
  progressCheckData: AllProgressCheckStatuses[] | null,
  program: ProgramData | undefined
): ProgressCheckStatus | null {
  if (program && progressCheckData) {
    const allProgressCheckStatuses = progressCheckData.find((item) => item.programID === program.ID);
    const progressCheckStatus = allProgressCheckStatuses?.pCs.find((item) => item.pCId === progressCheckID);

    return progressCheckStatus ?? null;
  }

  return null;
}

export function getProgressCheckCycle(
  progressCheckData: AllProgressCheckStatuses[] | null,
  program: ProgramData | undefined
): number | undefined {
  if (program && progressCheckData) {
    const allProgressCheckStatuses = progressCheckData.find((item) => item.programID === program.ID);
    const unfilteredCycles = allProgressCheckStatuses?.pCs.map((item) => item.submissions.cycle) ?? [];
    const cycles = unfilteredCycles.filter((item) => item !== undefined).map((item) => item!);

    if (cycles.length === 0) {
      return undefined;
    }

    return Math.max(...cycles) ?? 0;
  }

  return undefined;
}

/**
 * Gets the correct information for the ProgressCheckButton label
 * @param progressCheck - The Progress Check to process
 * @param progressCheckStatus - The status object of the Progress Check
 * @param programEvidence - The Evidence for the Progress Check
 * @param progressChecks - All of the Progress Checks
 * @param allProgressCheckData - The status objects for all the Progress Checks
 * @returns an object with a label and status. e.g. { label: "Approved", status:"Completed" }
 */
export function getProgressCheckLabelInformationForTelTriage(
  currentProgressCheck: ProgressCheck,
  programEvidence: IEvidence[],
  previousProgressCheck?: ProgressCheck
) {
  const evidenceForProgressCheck = programEvidence.filter((e) => {
    const parsedEvidence = e.evidenceJSON;
    if (!e.draft && parsedEvidence.programInfo.progressCheckID === currentProgressCheck.ID) {
      return true;
    }
  });

  let previousProgressCheckIsIncomplete = false;
  if (previousProgressCheck) {
    const evidenceForPreviousProgressCheck = programEvidence.filter((e) => {
      const parsedEvidence = e.evidenceJSON;
      const onHoldReset = parsedEvidence?.OnHoldReset ?? 0;
      if (!e.draft && onHoldReset === 0 && parsedEvidence.programInfo.progressCheckID === previousProgressCheck.ID) {
        return true;
      }
    });
    const previousProgressCheckIsApproved = isProgressCheckApprovedBasedOnCompTrigger(
      evidenceForPreviousProgressCheck,
      previousProgressCheck["Competence/Activity"]
    );
    previousProgressCheckIsIncomplete = !previousProgressCheckIsApproved;
  }

  const approved = isProgressCheckApprovedBasedOnCompTrigger(
    evidenceForProgressCheck,
    currentProgressCheck["Competence/Activity"]
  );

  const onHoldResetExists = evidenceForProgressCheck.find((e) => {
    const parsed = e.evidenceJSON;
    if (parsed && parsed.OnHoldReset && parsed.OnHoldReset == 1) {
      return true;
    }
  });

  if (
    !evidenceForProgressCheck.length &&
    (currentProgressCheck.SequentiallyLocked === SequentiallyLocked.Unlocked ||
      currentProgressCheck.SequentiallyLocked === SequentiallyLocked.UnlockedIgnore)
  ) {
    return {
      label: "Open",
      status: "Open",
    };
  }

  if (approved) {
    return {
      label: "Approved",
      status: "Completed",
    };
  } else if (!approved && onHoldResetExists) {
    return {
      label: "Reset",
      status: "Reset",
    };
  } else if (evidenceForProgressCheck.length > 0) {
    return {
      label: "In progress",
      status: "In progress",
    };
  } else if (previousProgressCheckIsIncomplete) {
    return {
      label: "Locked",
      status: "Locked",
    };
  } else {
    return {
      label: "Not started",
      status: "Not started",
    };
  }
}

export function getProgressCheckLabelInformation(
  progressCheck: ProgressCheck,
  progressCheckStatus: ProgressCheckStatus | undefined,
  programEvidence: IEvidence[],
  progressChecks: ProgressCheck[],
  allProgressCheckData: AllProgressCheckStatuses | null,
  programID: string,
  fastTrackActive: boolean,
  pauseAndHold: boolean
): IProgressCheckLabel | undefined {
  if (progressCheckStatus?.submissions.status === SubmissionState.Approved) {
    return {
      label: SubmissionState.Approved,
      status: "Completed",
    };
  } else if (progressCheckStatus?.submissions.status === SubmissionState.Completed) {
    return {
      label: SubmissionState.Completed,
      status: "Completed",
    };
  } else if (progressCheckStatus?.submissions.status === SubmissionState.Submitted) {
    const pcEvidence = getEvidenceForProgressCheck(programEvidence, progressCheck.ID);
    const endOfYearReviewIDs = [
      UOC_PROGRAM_EVIDENCE_DEFINITION_IDS.LEVEL_5_END_OF_YEAR_REVIEW,
      UOC_PROGRAM_EVIDENCE_DEFINITION_IDS.LEVEL_6_END_OF_YEAR_REVIEW,
    ];
    const hasApprovedEndOfYearReview = pcEvidence.some(
      (e) =>
        endOfYearReviewIDs.includes(e.evidenceJSON?.programInfo?.programEvidenceDefinitionID) &&
        e.evidenceJSON?.programInfo?.approvalState === "Approved"
    );
    if (hasApprovedEndOfYearReview) {
      return {
        label: "Waiting for academic review",
        status: "In progress",
      };
    }
    return {
      label: SubmissionState.Submitted,
      status: "In progress",
    };
  } else if (progressCheckStatus?.submissions.status === SubmissionState.Rejected) {
    return {
      label: SubmissionState.Rejected,
      status: "Attention",
    };
  }

  const progressCheckEvidence = getEvidenceForProgressCheck(programEvidence, progressCheck.ID);

  let finalProgressCheckStatus: string | undefined = undefined;

  const completionProgressCheck = progressChecks?.find((item) => item.ApprovalCompletesPM);

  if (completionProgressCheck) {
    finalProgressCheckStatus = allProgressCheckData?.pCs.find((item) => item.pCId === completionProgressCheck.ID)
      ?.submissions.status;
  }

  const completionPCLocked =
    finalProgressCheckStatus === SubmissionState.Approved ||
    (finalProgressCheckStatus === SubmissionState.Submitted &&
      Boolean(completionProgressCheck) &&
      getEvidenceForProgressCheck(programEvidence, completionProgressCheck!.ID).some(
        (e) =>
          e.evidenceJSON?.programInfo?.programEvidenceDefinitionID ===
            UOC_PROGRAM_EVIDENCE_DEFINITION_IDS.LEVEL_6_END_OF_YEAR_REVIEW &&
          e.evidenceJSON?.programInfo?.approvalState === "Approved"
      ));

  // --- NEW: Check if locked, regardless of evidence ---
  const progressCheckLocked = checkIfProgressCheckIsLocked(
    progressCheck,
    progressChecks,
    programEvidence,
    allProgressCheckData,
    programID,
    fastTrackActive,
    pauseAndHold
  );
  if (progressCheckLocked) {
    // If there is evidence, show Locked as status
    return {
      label: "Locked",
      status: "Locked",
    };
  }
  // --- END NEW ---

  if (progressCheckEvidence.length > 0) {
    const completed = isAllRequiredEvidenceAddedForProgressCheck(
      progressCheckEvidence,
      progressCheck["Competence/Activity"]
    );

    if (completionPCLocked) {
      return {
        label: "Locked",
        status: "Locked",
      };
    }

    if (completed) {
      if (progressCheck.Approval === ProgressCheckApproval.NotRequired) {
        if (
          progressCheck.SequentiallyLocked === SequentiallyLocked.Unlocked ||
          progressCheck.SequentiallyLocked === SequentiallyLocked.UnlockedIgnore
        ) {
          return {
            label: "Open",
            status: "Open",
          };
        }

        return {
          label: "Completed",
          status: "Completed",
        };
      } else if (progressCheckStatus?.submissions.status === SubmissionState.Submitted) {
        return {
          label: ProgressState.Submitted,
          status: ProgressState.InProgress,
        };
      } else if (progressCheckStatus?.submissions.status === SubmissionState.OnHold) {
        return {
          label: "Submission held",
          status: "Attention",
        };
      } else if (progressCheckStatus?.submissions.status === SubmissionState.OnHoldReset) {
        return {
          label: "Submission held",
          status: "Attention",
        };
      } else if (showProgressCheckEvidenceCount(progressCheck.ID)) {
        return {
          label: `${progressCheckEvidence.length}/${progressCheck["Competence/Activity"].length}`,
          status: "Completed",
        };
      } else {
        return {
          label: isUOCApprenticeship(programID) ? "In progress" : "Ready for submission",
          status: ProgressState.InProgress,
        };
      }
    } else if (showProgressCheckEvidenceCount(progressCheck.ID)) {
      return {
        label: `${progressCheckEvidence.length}/${progressCheck["Competence/Activity"].length}`,
        status: "In progress",
      };
    } else if (progressCheckStatus?.submissions.status === SubmissionState.OnHoldReset) {
      return {
        label: "Submission held",
        status: "Attention",
      };
    } else if (progressCheckStatus?.submissions.status === SubmissionState.OnHold) {
      return {
        label: "Submission held",
        status: "Attention",
      };
    } else if (progressCheckStatus?.submissions.status === SubmissionState.Submitted) {
      return {
        label: ProgressState.Submitted,
        status: ProgressState.InProgress,
      };
    } else {
      return {
        label: "In progress",
        status: ProgressState.InProgress,
      };
    }
  }

  if (completionProgressCheck && completionPCLocked) {
    return {
      label: "Locked",
      status: "Locked",
    };
  }

  if (
    progressCheck.SequentiallyLocked === SequentiallyLocked.Unlocked ||
    progressCheck.SequentiallyLocked === SequentiallyLocked.UnlockedIgnore
  ) {
    return {
      label: "Open",
      status: "Open",
    };
  }

  return {
    label: "Not started",
    status: "Not started",
  };
}

/**
 * @param submissionState - The current ProgressCheckSubmission object
 * @returns The CSS class for the submission state button
 */
export function getProgressCheckSubmissionButtonClass(
  submissionState: ProgressCheckSubmission | undefined
): string | undefined {
  if (submissionState) {
    if (submissionState.status === SubmissionState.Submitted) {
      return "!program-card-button-submitted";
    }

    if (submissionState.status === SubmissionState.OnHold) {
      return "!program-card-button-cta";
    }

    if (submissionState.status === SubmissionState.OnHoldReset) {
      return "!program-card-button-cta";
    }

    if (submissionState.status === SubmissionState.Approved) {
      return "!program-card-button-approved";
    }

    if (submissionState.status === SubmissionState.Rejected) {
      return "!program-card-button-rejected";
    }
  }

  return undefined;
}

/**
 * Gets the submission button text for the current progress check submission state
 * @param submissionState
 * @returns Submitted | Waiting for approval | Resubmit
 */
export function getProgressCheckSubmissionButtonText(submissionState: ProgressCheckSubmission | undefined): string {
  if (submissionState) {
    if (submissionState.status === SubmissionState.Submitted) {
      return SubmissionButtonState.WaitingForApproval;
    }

    if (submissionState.status === SubmissionState.OnHold) {
      return SubmissionButtonState.Resubmit;
    }

    if (submissionState.status === SubmissionState.OnHoldReset) {
      return SubmissionButtonState.Resubmit;
    }

    if (submissionState.status === SubmissionState.Approved) {
      return SubmissionButtonState.Approved;
    }

    if (submissionState.status === SubmissionState.Rejected) {
      return SubmissionButtonState.Rejected;
    }
  }

  return SubmissionButtonState.Submit;
}

/**
 * @param submissionState - The current ProgressCheckSubmission object
 * @returns The CSS class for the submission state button text
 */
export function getProgressCheckSubmissionButtonTextClass(
  submissionState: ProgressCheckSubmission | undefined
): string | undefined {
  if (submissionState) {
    if (submissionState.status === SubmissionState.Submitted) {
      return "!text-orange-text";
    }

    if (submissionState.status === SubmissionState.OnHold) {
      return "!text-white";
    }

    if (submissionState.status === SubmissionState.OnHoldReset) {
      return "!text-white";
    }

    if (submissionState.status === SubmissionState.Approved) {
      return "!text-green-text";
    }

    if (submissionState.status === SubmissionState.Rejected) {
      return "!text-white";
    }
  }

  return undefined;
}

/**
 * @param status - The current ProgressCheckSubmission state
 * @returns The Tailwind extended colour class name
 */
export function getProgressStatusBackgroundColor(status: string): string {
  if (status === ProgressState.NotStarted) {
    return "grey-30";
  }

  if (status === ProgressState.InProgress) {
    return "orange-20";
  }

  if (status === ProgressState.Submitted) {
    return "orange-20";
  }

  if (status === ProgressState.WaitingForAcademicReview) {
    return "orange-20";
  }

  if (status === ProgressState.ReadyForSubmission) {
    return "orange-20";
  }

  if (status === ProgressState.OnHold) {
    return "cta-red";
  }

  if (status === ProgressState.Rejected) {
    return "cta-red";
  }

  return "grey-30";
}

/**
 * @param status - The current ProgressCheckSubmission state
 * @returns The Tailwind extended colour class name
 * @returns
 */
export function getProgressStatusTextColor(status: string): string {
  if (status === ProgressState.NotStarted) {
    return "text-grey-60";
  }

  if (status === ProgressState.InProgress) {
    return "text-orange-text";
  }

  if (status === ProgressState.Submitted) {
    return "text-orange-text";
  }

  if (status === ProgressState.WaitingForAcademicReview) {
    return "text-orange-text";
  }

  if (status === ProgressState.ReadyForSubmission) {
    return "text-orange-text";
  }

  if (status === ProgressState.OnHold) {
    return "text-white";
  }

  if (status === ProgressState.Rejected) {
    return "text-white";
  }

  return "text-grey-60";
}

/**
 * Returns roles label for the program details component
 * @param programID - The current program ID
 * @returns
 */
export function getRolesLabel(programID: string): string {
  const programsWithMandatoryLabel = [
    PROGRAM_IDS.NWAS_EPRR,
    PROGRAM_IDS.SWAST_EPRR,
    PROGRAM_IDS.NEAS_EPRR,
    PROGRAM_IDS.TEST_PROGRAM,
    PROGRAM_IDS.SWAST_SP_AP_RP,
  ];
  if (programsWithMandatoryLabel.includes(programID)) {
    return "Your role (Mandatory)";
  }
  return "Your roles";
}

export function getTeamsLabel(programID: string): string {
  const programsWithMandatoryLabel = [PROGRAM_IDS.SWAST_SP_AP_RP];
  if (programsWithMandatoryLabel.includes(programID)) {
    return "Your team (Mandatory)";
  }
  return "Your team";
}

export function getAreaLabel(programID: string): string {
  const programsWithMandatoryLabel = [PROGRAM_IDS.LAS_NQP, PROGRAM_IDS.NWAS_ARC];
  if (programsWithMandatoryLabel.includes(programID)) {
    return "Area (Mandatory)";
  }
  return "Area";
}

/**
 *
 * @param user - The current user object
 * @returns The selected role id if the user exists, otherwise returns an empty string
 */
export function getSelectedRoleIdForEPRRProgram(user: IUser | null, programId: string) {
  if (!user) return "";

  const prog = user.programRoles?.find((role) => role.programID === programId);
  const selectedRoleID = prog?.programRoles[0].roleID;

  return selectedRoleID;
}

/**
 * Checks whether the currently selected program requires user setup
 * @param programID - The current program ID
 * @returns
 */

/**
 * Checks whether the expiry date for a certificate input should be hidden.
 * @param programID - The current program ID
 * @returns
 */
export function hideExpiryDateCertInput(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_EPRR, PROGRAM_IDS.SWAST_EPRR, PROGRAM_IDS.NEAS_EPRR, PROGRAM_IDS.TEST_PROGRAM].includes(
    programID
  );
}

export function hideProgramRoles(programID: string): boolean {
  return [PROGRAM_IDS.NEAS_NQP, PROGRAM_IDS.UOC_APPRENTICESHIP].includes(programID);
}

/**
 * Checks whether the program card should show standards and evidence details (used by EPRR)
 * @param programID - The current program ID
 * @returns
 */
export function isNwasEPRR(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_EPRR].includes(programID);
}

export function isSwastEPRR(programID: string): boolean {
  return [PROGRAM_IDS.SWAST_EPRR].includes(programID);
}

export function isSwastEprrNosCompliant(percentage: number): boolean {
  return percentage >= 100;
}

export function getSwastEprrNosCompliancePillClasses(percentage: number): {
  backgroundColor: string;
  textColor: string;
} {
  if (isSwastEprrNosCompliant(percentage)) {
    return {
      backgroundColor: "bg-standard-compliant",
      textColor: "text-standard-compliant-text",
    };
  }

  return {
    backgroundColor: "bg-standard-not-compliant",
    textColor: "text-standard-not-compliant-text",
  };
}

export function getSwastEprrNosProgressTextClass(percentage: number): string {
  if (isSwastEprrNosCompliant(percentage)) {
    return "text-progress-compliant-text";
  }

  return "text-progress-not-compliant-text";
}

export function isNeasEPRR(programID: string): boolean {
  return [PROGRAM_IDS.NEAS_EPRR].includes(programID);
}

export function isNwasARC(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_ARC].includes(programID);
}

export function isTestEPRR(programID: string): boolean {
  return [PROGRAM_IDS.TEST_PROGRAM].includes(programID);
}

export function isUOCApprenticeship(programID: string): boolean {
  return [PROGRAM_IDS.UOC_APPRENTICESHIP].includes(programID);
}

export function isUOCProgramLockedByEoYReview(
  programID: string,
  progressChecks: ProgressCheck[],
  allProgressCheckData: AllProgressCheckStatuses[] | null | undefined,
  allEvidence: IEvidence[],
  userLevel: string
): boolean {
  if (!isUOCApprenticeship(programID)) return false;

  const endOfYearReviewIDs = [
    UOC_PROGRAM_EVIDENCE_DEFINITION_IDS.LEVEL_5_END_OF_YEAR_REVIEW,
    UOC_PROGRAM_EVIDENCE_DEFINITION_IDS.LEVEL_6_END_OF_YEAR_REVIEW,
  ];

  const dataForProgram = allProgressCheckData?.find((item) => item.programID === programID);

  return progressChecks.some((pc) => {
    if (!userLevel.startsWith(pc.Name)) return false;

    const pcStatus = dataForProgram?.pCs.find((item) => item.pCId === pc.ID)?.submissions.status;

    if (pcStatus !== SubmissionState.Submitted && pcStatus !== SubmissionState.Approved) {
      return false;
    }

    return getEvidenceForProgressCheck(allEvidence, pc.ID).some(
      (e) =>
        endOfYearReviewIDs.includes(e.evidenceJSON?.programInfo?.programEvidenceDefinitionID) &&
        e.evidenceJSON?.programInfo?.approvalState === "Approved"
    );
  });
}

export function isEPRR(programID?: string) {
  if (!programID) return false;
  return [PROGRAM_IDS.NWAS_EPRR, PROGRAM_IDS.SWAST_EPRR, PROGRAM_IDS.NEAS_EPRR, PROGRAM_IDS.TEST_PROGRAM].includes(
    programID
  );
}

// TODO combine isDetailsSet functions into single function
/**
 * Checks whether the user details are set for the NWAS EPRR Program Module
 * @param user - The current user object
 * @returns
 */
export function isNwasEprrDetailsSet(user: any) {
  if (user && user.programRoles) {
    const eprrProgram = user.programRoles.find((program: any) => program.programID === PROGRAM_IDS.NWAS_EPRR);
    return eprrProgram ? true : false;
  }

  return false;
}

/**
 * Checks whether the user details are set for the SWAST EPRR Program Module
 * @param user - The current user object
 * @returns
 */
export function isSwastEprrDetailsSet(user: any) {
  if (user && user.programRoles) {
    const eprrProgram = user.programRoles.find((program: any) => program.programID === PROGRAM_IDS.SWAST_EPRR);
    return eprrProgram ? true : false;
  }

  return false;
}

/**
 * Checks whether the user details are set for the NEAS EPRR Program Module
 * @param user - The current user object
 * @returns
 */
export function isNeasEprrDetailsSet(user: any) {
  if (user && user.programRoles) {
    const eprrProgram = user.programRoles.find((program: any) => program.programID === PROGRAM_IDS.NEAS_EPRR);
    return eprrProgram ? true : false;
  }

  return false;
}

/**
 * Checks whether the user details are set for the TEST EPRR Program Module
 * @param user - The current user object
 * @returns
 */
export function isTestEprrDetailsSet(user: any) {
  if (user && user.programRoles) {
    const program = user.programRoles.find((program: any) => program.programID === PROGRAM_IDS.TEST_PROGRAM);
    return program ? true : false;
  }

  return false;
}

export function isClinicalAdvisorsDetailsSet(user: any) {
  if (user && user.programRoles) {
    const program = user.programRoles.find((program: any) => program.programID === PROGRAM_IDS.LAS_CA);
    return program ? true : false;
  }

  return false;
}

export function isNwasARCDetailsSet(user: any) {
  if (user && user.programRoles) {
    const program = user.programRoles.find((program: any) => program.programID === PROGRAM_IDS.NWAS_ARC);
    return program?.programRoles && program?.area ? true : false;
  }

  return false;
}

export function isNQPDetailsSet(user: any, programID: string) {
  if (user && user.programRoles) {
    const program = user.programRoles.find((program: any) => program.programID === programID);
    return program ? true : false;
  }

  return false;
}

export function isMtsDetailsSet(user: any) {
  if (user && user.programRoles) {
    const program = user.programRoles.find((program: any) => program.programID === PROGRAM_IDS.LAS_MTS);
    return program ? true : false;
  }

  return false;
}

export function isChubDetailsSet(user: any) {
  if (user && user.programRoles) {
    const program = user.programRoles.find((program: any) => program.programID === PROGRAM_IDS.LAS_CHUB);
    return program ? true : false;
  }

  return false;
}

export function isCsmDetailsSet(user: any) {
  if (user && user.programRoles) {
    const csmProgram = user.programRoles.find((program: any) => program.programID === PROGRAM_IDS.LAS_CSM);
    return csmProgram ? true : false;
  }

  return false;
}

export function isSPAPRPDetailsSet(user: any) {
  if (user && user.programRoles) {
    const program = user.programRoles.find((program: any) => program.programID === PROGRAM_IDS.SWAST_SP_AP_RP);
    return program ? true : false;
  }

  return false;
}

/**
 * Checks whether the details card should be displayed
 * @param programID - The current program ID
 * @returns
 */
export function isPreceptorship(programID: string): boolean {
  return [PROGRAM_IDS.SWAST_PRECEPTORSHIP].includes(programID);
}

export function isNQPProgram(programID: string): boolean {
  return [
    PROGRAM_IDS.LAS_NQP, // LAS NQP
    PROGRAM_IDS.NEAS_NQP, // NEAS NQP
    PROGRAM_IDS.NWAS_NQP, // NWAS NQP
    PROGRAM_IDS.SWAST_NQP2, // SWAST NQP 2
    PROGRAM_IDS.SWAST_SP_AP_RP, // SWASFT SP/AP/RP
    PROGRAM_IDS.YAS_NQP, // YAS NQP
  ].includes(programID);
}

/**
 * Checks whether the chosen Progress Check is finished (Approved or Completed)
 * @param submissionState - The submission history for the chosen Progress Check
 * @returns
 */
export function isProgressCheckFinished(submissionState: ProgressCheckSubmission | undefined): boolean {
  return submissionState?.status === SubmissionState.Approved || submissionState?.status === SubmissionState.Completed;
}

/**
 * @param progressCheckEvidence - The Evidence for the current Progress Check
 * @param competences - All of the Progress Checks in the Program Module
 * @returns Whether all of the Evidence is added for the Progress Check
 */
export function isAllRequiredEvidenceAddedForProgressCheck(
  progressCheckEvidence: IEvidence[],
  competences: CompetenceActivityClass[],
  isNQP?: boolean
): boolean {
  let evidenceForProgressCheck: IEvidence[] = _.cloneDeep(progressCheckEvidence) ?? [];

  evidenceForProgressCheck = evidenceForProgressCheck?.filter((item) => {
    const evidenceJSON = item.evidenceJSON;

    const onHoldReset = evidenceJSON?.OnHoldReset === 1;

    return !onHoldReset && item.draft !== true;
  });

  const allCompetences = _.cloneDeep(competences);

  const progressCheckIDs = evidenceForProgressCheck
    .map((item: IEvidence) => {
      const evidenceJSON = item.evidenceJSON;
      return evidenceJSON?.programInfo?.progressCheckCompetenceID;
    })
    .sort();

  const requiredCompetences = allCompetences.filter((competence: CompetenceActivityClass) => {
    const isWellbeing = competence.isWellbeingActivity;

    if (isNQP && isWellbeing) {
      return !isWellbeing;
    }

    const hasNumberRequired = typeof competence["Number Evidences required"] === "number";

    const numberRequired = (competence["Number Evidences required"] ?? 0) > 0;

    return !hasNumberRequired || numberRequired;
  });

  const competenceIDs = requiredCompetences.map((item) => item.ID).sort();

  let allEvidenceAdded = false;

  if (requiredCompetences.length !== 0) {
    const allCompetencesAdded = competenceIDs.every((id) => progressCheckIDs.includes(id));

    allEvidenceAdded = allCompetencesAdded;
  }

  return allEvidenceAdded;
}

export function isAllEvidenceAddedForProgressCheck(
  progressCheckEvidence: IEvidence[],
  competences: CompetenceActivityClass[],
  progressCheck: ProgressCheck,
  isNQP?: boolean
): boolean {
  let evidenceForProgressCheck: IEvidence[] = _.cloneDeep(progressCheckEvidence) ?? [];

  evidenceForProgressCheck = evidenceForProgressCheck?.filter((item) => {
    const evidenceJSON = item.evidenceJSON;

    const onHoldReset = evidenceJSON?.OnHoldReset === 1;

    return !onHoldReset && item.draft !== true;
  });

  const allCompetences = _.cloneDeep(competences);

  const progressCheckIDs = evidenceForProgressCheck
    .map((item: IEvidence) => {
      const evidenceJSON = item.evidenceJSON;
      return evidenceJSON?.programInfo?.progressCheckCompetenceID;
    })
    .sort();

  const requiredCompetences = allCompetences.filter((competence: CompetenceActivityClass) => {
    const isWellbeing = competence.isWellbeingActivity;

    if (isNQP && isWellbeing) {
      return !isWellbeing;
    }

    return true;
  });

  const competenceIDs = requiredCompetences.map((item) => item.ID).sort();

  let allEvidenceAdded = false;

  if (requiredCompetences.length !== 0) {
    const allCompetencesAdded = competenceIDs.every((id) => progressCheckIDs.includes(id));

    allEvidenceAdded = allCompetencesAdded;
  }

  if (allEvidenceAdded && progressCheck.KeepOpenOnCompletion) {
    const hasMultipleAllowed = allCompetences.some((competence) => competence.MultipleAllowed);

    if (hasMultipleAllowed) {
      return false;
    }
  }

  // TODO: Add check for multiple allowed if required, currently button is disabled if all evidence is added

  return allEvidenceAdded;
}

/**
 *
 * @param allEvidenceAdded
 * @param approval
 * @returns true if all Evidence is added for the Progress Check and it doesn't require approval
 */
export function isProgressCheckCompleted(
  allEvidenceAdded: boolean,
  approval: string,
  keepOpenOnCompletion: boolean,
  submissionState?: ProgressCheckSubmission | undefined
): boolean {
  if (submissionState?.status === SubmissionState.Completed) {
    if (keepOpenOnCompletion) {
      return false;
    }

    return true;
  }

  return allEvidenceAdded && approval === ProgressCheckApproval.NotRequired;
}

// Checks if a progress check is completed based on it's evidence. This function should be used for program modules that are approved
// based on a competence trigger. For example, the data in the "End of Course Review" competence in MTS Telephone Triage acts as a trigger for approval.
export function isProgressCheckApprovedBasedOnCompTrigger(
  evidence: IEvidence[],
  progressCheckComps: CompetenceActivityClass[]
): boolean {
  if (!evidence) return false; // check for falsy evidence value because app can get into weird state where this is undefined

  let result = false;
  const compUsedForApproval = progressCheckComps.find((comp) => comp.isFinalReview);
  const migrationCompUsedForApproval = progressCheckComps.find((comp) => comp.isForMigration);

  evidence
    .filter((e) => !e.draft)
    .forEach((e) => {
      if (e.evidenceJSON) {
        const parsedEvidence = e.evidenceJSON;
        if (parsedEvidence.programInfo) {
          if (
            parsedEvidence.programInfo.progressCheckCompetenceID === compUsedForApproval?.ID &&
            compUsedForApproval?.["Evidence Definitions"][0]
          ) {
            // check if end of review is done
            const evidenceDef = compUsedForApproval["Evidence Definitions"][0];
            const part = evidenceDef["Evidence parts"]?.find((part) => part["Progress Check Submission Trigger"]);
            if (part) {
              if (parsedEvidence[part?.Name] === part["Progress Check Submission Trigger"]) {
                result = true;
              }
            }
          } else if (
            parsedEvidence.programInfo.progressCheckCompetenceID &&
            parsedEvidence.programInfo.progressCheckCompetenceID === migrationCompUsedForApproval?.ID
          ) {
            // check if migration competence ('Evidence of Previous Learning') is done
            result = true;
          }
        }
      }
    });

  return result;
}

export function isProgressCheckUnlockedBasedOnCompetenceTrigger(
  programEvidence: IEvidence[],
  progressCheck: ProgressCheck,
  previousProgressCheck: ProgressCheck,
  progressCheckComps: CompetenceActivityClass[]
): boolean {
  if (!programEvidence) return false;

  let result = false;
  const compUsedForUnlock = progressCheckComps.find((comp) => comp.ID === progressCheck.UnlockedByCompetenceID);

  programEvidence
    .filter((e) => !e.draft)
    .forEach((e) => {
      if (e.evidenceJSON) {
        const parsedEvidence = e.evidenceJSON;
        if (parsedEvidence.programInfo) {
          if (
            parsedEvidence.programInfo.progressCheckCompetenceID === compUsedForUnlock?.ID &&
            compUsedForUnlock?.["Evidence Definitions"][0]
          ) {
            // Check if competence that unlocks the progress check is done
            const evidenceDef = compUsedForUnlock["Evidence Definitions"][0];
            const part = evidenceDef["Evidence parts"]?.find((part) => part["Progress Check Unlock Trigger"]);
            if (part) {
              if (parsedEvidence[part?.Name] === part["Progress Check Unlock Trigger"]) {
                result = true;
              }
            }
          }
        }
      }
    });

  return result;
}

/**
 * @param progressCheckCompetenceID
 * @param evidenceDefinitionID
 * @returns true if the field is locked in the Evidence Modal to prevent user input (ConfidenceLevelGraph)
 */
export function isFieldLocked(progressCheckCompetenceID: string | undefined, evidenceDefinitionID: string): boolean {
  if (!progressCheckCompetenceID) {
    return false;
  }

  const competenceIncluded = [
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.SWAST_PRECEPTORSHIP_7_SHIFTS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.SWAST_PRECEPTORSHIP_14_SHIFTS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.SWAST_PRECEPTORSHIP_END,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.NEAS_NQP_6_12_MONTHS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.NEAS_NQP_12_18_MONTHS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.NEAS_NQP_18_24_MONTHS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.YAS_NQP_6_12_MONTHS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.YAS_NQP_12_18_MONTHS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.YAS_NQP_18_24_MONTHS,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.LAS_NQP_12_MONTHS_REVIEW,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.LAS_NQP_18_MONTHS_REVIEW,
    NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.LAS_NQP_24_MONTHS_REVIEW,
  ].includes(progressCheckCompetenceID);

  const partCanBeLocked = [
    PROGRAM_EVIDENCE_PART_IDS.SWAST_PRECEPTORSHIP_AREA_OF_PRACTICE_1,
    PROGRAM_EVIDENCE_PART_IDS.SWAST_PRECEPTORSHIP_AREA_OF_PRACTICE_2,
    PROGRAM_EVIDENCE_PART_IDS.NEAS_NQP_AREA_OF_PRACTICE_1,
    PROGRAM_EVIDENCE_PART_IDS.NEAS_NQP_AREA_OF_PRACTICE_2,
    PROGRAM_EVIDENCE_PART_IDS.YAS_NQP_AREA_OF_PRACTICE_1,
    PROGRAM_EVIDENCE_PART_IDS.YAS_NQP_AREA_OF_PRACTICE_2,
    PROGRAM_EVIDENCE_PART_IDS.LAS_NQP_AREA_OF_PRACTICE_1,
    PROGRAM_EVIDENCE_PART_IDS.LAS_NQP_AREA_OF_PRACTICE_2,
  ].includes(evidenceDefinitionID);

  return competenceIncluded && partCanBeLocked;
}

export function autoCompleteProgressCheckDependsOnTrigger(programID: string) {
  return [PROGRAM_IDS.LAS_MTS, PROGRAM_IDS.LAS_CHUB, PROGRAM_IDS.LAS_CA, PROGRAM_IDS.LAS_CSM].includes(programID);
}

export function includeSubtitleInProgressCheckEvidenceButton(programID: string): boolean {
  return [
    PROGRAM_IDS.LAS_CA,
    PROGRAM_IDS.LAS_CHUB,
    PROGRAM_IDS.LAS_CSM,
    PROGRAM_IDS.LAS_MTS,
    PROGRAM_IDS.LAS_NQP,
    PROGRAM_IDS.NEAS_NQP,
    PROGRAM_IDS.NWAS_NQP,
    PROGRAM_IDS.SWAST_NQP2,
    PROGRAM_IDS.SWAST_SP_AP_RP,
    PROGRAM_IDS.YAS_NQP,
    PROGRAM_IDS.UOC_APPRENTICESHIP,
  ].includes(programID);
}

export function removeSubtitleForManuallySelectedLOs(programID: string): boolean {
  return [PROGRAM_IDS.NEAS_NQP, PROGRAM_IDS.YAS_NQP].includes(programID);
}

export function removeMandatoryCountInProgressCheckEvidenceButton(programID: string): boolean {
  return [
    PROGRAM_IDS.LAS_NQP,
    PROGRAM_IDS.NEAS_NQP,
    PROGRAM_IDS.NWAS_NQP,
    PROGRAM_IDS.SWAST_NQP2,
    PROGRAM_IDS.SWAST_SP_AP_RP,
    PROGRAM_IDS.YAS_NQP,
  ].includes(programID);
}

export function showApprovalStatusBannerOnProgressCheckPage(programID: string): boolean {
  return [PROGRAM_IDS.LAS_CA, PROGRAM_IDS.LAS_CHUB, PROGRAM_IDS.LAS_CSM, PROGRAM_IDS.LAS_MTS].includes(programID);
}

/**
 * @param submissionState
 * @returns true if the submission state is approved
 */
export function isProgressCheckApproved(submissionState: ProgressCheckSubmission | undefined): boolean {
  return submissionState?.status === SubmissionState.Approved;
}

/**
 * @param submissionState
 * @returns true if the submission state is rejected
 */
export function isProgressCheckRejected(submissionState: ProgressCheckSubmission | undefined): boolean {
  return submissionState?.status === SubmissionState.Rejected;
}

/**
 * @param submissionState
 * @param allEvidenceAdded
 * @returns true if the submission state is submitted or approved. Otherwise returns whether all the required evidence is not added
 */
export function isProgressCheckButtonDisabled(
  submissionState: ProgressCheckSubmission | undefined,
  allEvidenceAdded: boolean
): boolean {
  if (submissionState) {
    if (submissionState.status === SubmissionState.Submitted || submissionState.status === SubmissionState.Approved) {
      return true;
    }
  }

  return !allEvidenceAdded;
}

export function isProgressCheckSubmissionButtonDisabled(
  submissionState: ProgressCheckSubmission | undefined,
  progressCheckComps: CompetenceActivityClass[],
  evidence: IEvidence[],
  allEvidenceAdded: boolean,
  fastTrackActive: boolean = false,
  isNQP?: boolean
): boolean {
  if (submissionState) {
    if (submissionState.status === SubmissionState.Submitted || submissionState.status === SubmissionState.Approved) {
      return true;
    }
  }

  const compUsedForSubmission = progressCheckComps.find((comp) => comp.canUnlockSubmission);

  if (compUsedForSubmission) {
    let result = true;
    let evidenceAddedForCompUnlock = false;

    evidence
      .filter((e) => !e.draft)
      .forEach((e) => {
        if (e.evidenceJSON) {
          const parsedEvidence = e.evidenceJSON;
          const onHoldReset = parsedEvidence?.OnHoldReset ?? 0;

          if (
            onHoldReset !== 1 &&
            parsedEvidence.programInfo.progressCheckCompetenceID === compUsedForSubmission.ID &&
            compUsedForSubmission["Evidence Definitions"][0]
          ) {
            const evidenceDef = compUsedForSubmission["Evidence Definitions"][0];
            const part = evidenceDef["Evidence parts"]?.find(
              (part) => part["Progress Check Submission Unlock Trigger"]
            );

            evidenceAddedForCompUnlock = true;

            if (part) {
              if (parsedEvidence[part?.Name] === part["Progress Check Submission Unlock Trigger"]) {
                result = false;
              } else {
                if (isNQP && result) {
                  result = !allEvidenceAdded;
                }
              }
            }
          }
        }
      });

    if (evidenceAddedForCompUnlock) {
      return result;
    }
  }

  return !fastTrackActive && !allEvidenceAdded;
}

export function isWellbeingActivity(competenceID: string = "") {
  return [
    NQP_WELLBEING_ACTIVITY_IDS.SWAST_NQP,
    NQP_WELLBEING_ACTIVITY_IDS.NEAS_NQP,
    NQP_WELLBEING_ACTIVITY_IDS.NWAS_NQP,
    NQP_WELLBEING_ACTIVITY_IDS.YAS_NQP,
  ].includes(competenceID);
}

/**
 * Gets the evidence for generating the Preceptor confidence level graph and maps it to the ConfidenceLevelData object for the graph to use
 * @param confidenceLevelEvidence - The filtered evidence for the confidence level competences
 * @returns The mapped confidence level data if the first required evidence has been added, or null if it has not been added
 */
export function mapConfidenceLevelEvidence(
  confidenceLevelEvidence: IEvidence[],
  programID: string
): ConfidenceLevelData | null {
  if (programID === PROGRAM_IDS.NEAS_NQP) {
    // NEAS NQP
    const evidenceID = NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.NEAS_NQP_0_6_MONTHS;

    const prePreceptorEvidence = confidenceLevelEvidence.find((evidence: IEvidence) => {
      const evidenceJSON = evidence.evidenceJSON;
      const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID || "";

      const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

      return progressCheckCompetenceID === evidenceID && onHoldReset !== 1 && evidence.draft !== true;
    });

    if (prePreceptorEvidence) {
      const prePreceptorEvidenceJSON = prePreceptorEvidence.evidenceJSON;

      const area1Data = [0, 0, 0, 0];
      const area2Data = [0, 0, 0, 0];
      const generalData = [0, 0, 0, 0];

      for (let i = 0; i < confidenceLevelEvidence.length; i++) {
        const evidence = confidenceLevelEvidence[i];
        const evidenceJSON = evidence.evidenceJSON;

        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        // Don't include draft evidence or evidence on hold reset in the data
        if (onHoldReset === 1 || evidence.draft === true) {
          continue;
        }

        const index = getConfidenceLevelProgressCheckIndex(evidenceJSON.programInfo?.progressCheckID);

        if (index > -1) {
          if (
            evidenceJSON.programInfo.progressCheckCompetenceID ===
            NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.NEAS_NQP_0_6_MONTHS
          ) {
            area1Data[index] = parseInt(evidenceJSON["Confidence level 1"], 10);
            area2Data[index] = parseInt(evidenceJSON["Confidence level 2"], 10);
            generalData[index] = parseInt(evidenceJSON["General Confidence level"], 10);
          } else {
            area1Data[index] = parseInt(evidenceJSON["Confidence level 1"], 10);
            area2Data[index] = parseInt(evidenceJSON["Confidence level 2"], 10);
            generalData[index] = parseInt(evidenceJSON["General Confidence level"], 10);
          }
        }
      }

      return {
        area1: {
          areaName: prePreceptorEvidenceJSON["Area of practice 1 - pre-preceptorship"],
          data: area1Data,
        },
        area2: {
          areaName: prePreceptorEvidenceJSON["Area of practice 2 - pre-preceptorship"],
          data: area2Data,
        },
        general: {
          areaName: "General",
          data: generalData,
        },
      };
    }

    return null;
  } else if (programID === PROGRAM_IDS.YAS_NQP) {
    // YAS NQP
    const evidenceID = NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.YAS_NQP_0_6_MONTHS;
    const TLEvidenceID = NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.YAS_NQP_TL_SHIFT;

    const prePreceptorEvidence = confidenceLevelEvidence.find((evidence: IEvidence) => {
      const evidenceJSON = evidence.evidenceJSON;
      const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID || "";

      const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

      return progressCheckCompetenceID === evidenceID && onHoldReset !== 1 && evidence.draft !== true;
    });

    const TLShiftEvidence = confidenceLevelEvidence.find((evidence: IEvidence) => {
      const evidenceJSON = evidence.evidenceJSON;
      const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID || "";

      const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

      return progressCheckCompetenceID === TLEvidenceID && onHoldReset !== 1 && evidence.draft !== true;
    });

    if (prePreceptorEvidence || TLShiftEvidence) {
      const prePreceptorEvidenceJSON = prePreceptorEvidence && prePreceptorEvidence.evidenceJSON;

      const area1Data = [0, 0, 0, 0];
      const area2Data = [0, 0, 0, 0];
      const generalData = [0, 0, 0, 0];
      const TLData = [0, 0, 0, 0];
      for (let i = 0; i < confidenceLevelEvidence.length; i++) {
        const evidence = confidenceLevelEvidence[i];
        const evidenceJSON = evidence.evidenceJSON;

        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        // Don't include draft evidence or evidence on hold reset in the data
        if (onHoldReset === 1 || evidence.draft === true) {
          continue;
        }

        const index = getConfidenceLevelProgressCheckIndex(evidenceJSON.programInfo?.progressCheckID);

        if (index > -1) {
          if (
            evidenceJSON.programInfo.progressCheckCompetenceID ===
            NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.YAS_NQP_0_6_MONTHS
          ) {
            area1Data[index] = parseInt(evidenceJSON["Confidence level 1"], 10);
            area2Data[index] = parseInt(evidenceJSON["Confidence level 2"], 10);
            generalData[index] = parseInt(evidenceJSON["General Confidence level"], 10);
          } else if (
            evidenceJSON.programInfo.progressCheckCompetenceID === NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.YAS_NQP_TL_SHIFT
          ) {
            TLData[index] = parseInt(evidenceJSON["TL Shift Confidence Levels"], 10);
          } else {
            area1Data[index] = parseInt(evidenceJSON["Confidence level 1"], 10);
            area2Data[index] = parseInt(evidenceJSON["Confidence level 2"], 10);
            generalData[index] = parseInt(evidenceJSON["General Confidence level"], 10);
          }
        }
      }

      return {
        area1: {
          areaName: prePreceptorEvidenceJSON?.["Area of practice 1 - pre-preceptorship"] ?? "Unset",
          data: area1Data,
        },
        area2: {
          areaName: prePreceptorEvidenceJSON?.["Area of practice 2 - pre-preceptorship"] ?? "Unset",
          data: area2Data,
        },
        general: {
          areaName: "General",
          data: generalData,
        },
        TLData: {
          areaName: "TL Shift",
          data: TLData,
        },
      };
    }

    return null;
  } else if (programID === PROGRAM_IDS.SWAST_PRECEPTORSHIP) {
    // SWASFT Preceptorship NQP1
    const prePreceptorEvidenceID = NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.SWAST_PRECEPTORSHIP_PRE;

    const prePreceptorEvidence = confidenceLevelEvidence.find((evidence: IEvidence) => {
      const evidenceJSON = evidence.evidenceJSON;
      const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID || "";

      const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

      return progressCheckCompetenceID === prePreceptorEvidenceID && onHoldReset !== 1 && evidence.draft !== true;
    });

    if (prePreceptorEvidence) {
      const prePreceptorEvidenceJSON = prePreceptorEvidence.evidenceJSON;

      const area1Data = [0, 0, 0, 0];
      const area2Data = [0, 0, 0, 0];
      const generalData = [0, 0, 0, 0];

      for (let i = 0; i < confidenceLevelEvidence.length; i++) {
        const evidence = confidenceLevelEvidence[i];
        const evidenceJSON = evidence.evidenceJSON;

        const onHoldReset = evidenceJSON.OnHoldReset ?? 0;

        if (onHoldReset === 1 || evidence.draft === true) {
          continue;
        }

        const index = getConfidenceLevelProgressCheckIndex(evidenceJSON.programInfo?.progressCheckID);

        if (index > -1) {
          if (
            evidenceJSON.programInfo?.progressCheckCompetenceID ===
            NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.SWAST_PRECEPTORSHIP_GENERAL
          ) {
            generalData[index] = parseInt(evidenceJSON["Confidence level 1"]);
          } else if (
            evidenceJSON.programInfo?.progressCheckCompetenceID ===
            NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.SWAST_PRECEPTORSHIP_END
          ) {
            area1Data[index] = parseInt(evidenceJSON["End of Preceptorship confidence levels 1"]);
            area2Data[index] = parseInt(evidenceJSON["End of Preceptorship confidence levels 2"]);
          } else {
            area1Data[index] = parseInt(evidenceJSON["Confidence level 1"]);
            area2Data[index] = parseInt(evidenceJSON["Confidence level 2"]);
          }
        }
      }

      return {
        area1: {
          areaName: prePreceptorEvidenceJSON["Area of practice 1 - pre-preceptorship"],
          data: area1Data,
        },
        area2: {
          areaName: prePreceptorEvidenceJSON["Area of practice 2 - pre-preceptorship"],
          data: area2Data,
        },
        general: {
          areaName: "General",
          data: generalData,
        },
      };
    }

    return null;
  } else if (programID === PROGRAM_IDS.LAS_NQP) {
    // LAS NQP
    const evidenceID = NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.LAS_NQP_6_MONTH_REVIEW;

    const firstConfidenceLevelEvidence = confidenceLevelEvidence.find((evidence: IEvidence) => {
      const evidenceJSON = evidence.evidenceJSON;
      const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID || "";

      const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

      return progressCheckCompetenceID === evidenceID && onHoldReset !== 1 && evidence.draft !== true;
    });

    if (firstConfidenceLevelEvidence) {
      const firstConfidenceLevelEvidenceJSON = firstConfidenceLevelEvidence.evidenceJSON;

      const area1Data = [0, 0, 0, 0];
      const area2Data = [0, 0, 0, 0];
      const generalData = [0, 0, 0, 0];

      for (let i = 0; i < confidenceLevelEvidence.length; i++) {
        const evidence = confidenceLevelEvidence[i];
        const evidenceJSON = evidence.evidenceJSON;

        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        if (onHoldReset === 1 || evidence.draft === true) {
          continue;
        }

        const index = getConfidenceLevelProgressCheckIndex(evidenceJSON.programInfo?.progressCheckID);

        if (index > -1) {
          if (
            evidenceJSON.programInfo.progressCheckCompetenceID ===
            NQP_CONFIDENCE_LEVEL_COMPETENCY_IDS.LAS_NQP_6_MONTH_REVIEW
          ) {
            area1Data[index] = parseInt(evidenceJSON["Confidence level 1"], 10);
            area2Data[index] = parseInt(evidenceJSON["Confidence level 2"], 10);
            generalData[index] = parseInt(evidenceJSON["General Confidence level"], 10);
          } else {
            area1Data[index] = parseInt(evidenceJSON["Confidence level 1"], 10);
            area2Data[index] = parseInt(evidenceJSON["Confidence level 2"], 10);
            generalData[index] = parseInt(evidenceJSON["General Confidence level"], 10);
          }
        }
      }

      return {
        area1: {
          areaName: firstConfidenceLevelEvidenceJSON["Area of practice 1 - pre-preceptorship"],
          data: area1Data,
        },
        area2: {
          areaName: firstConfidenceLevelEvidenceJSON["Area of practice 2 - pre-preceptorship"],
          data: area2Data,
        },
        general: {
          areaName: "General",
          data: generalData,
        },
      };
    }

    return null;
  }

  return null;
}

export function mapProgramDataVersionInfo(data: ProgramData[]): ProgramDataVersionInfo[] {
  const object = _.cloneDeep(data);

  return object.map((program) => ({
    Name: program.Name,
    Trust: program.Trust,
    ID: program.ID,
    version: program.version,
  }));
}

/**
 * Tries to find the user's ESR number from the subscription that corresponds to the Program Module passed in
 * @param program - The current Program Module
 * @param subscriptions - All of the user's Salesforce subscriptions
 * @returns The ESR number if it exists, otherwise undefined
 */
export function mapESRNumber(program: ProgramData, subscriptions: IClassSubscription[]): string | undefined {
  if (program && subscriptions) {
    const programSubscriptionIDs = program?.Subscription ?? [];

    const programSubscription: IClassSubscription | undefined = subscriptions?.find(
      (item: IClassSubscription | any) => {
        const subscriptionPrograms: string[] = item.ParaFolioPrograms;

        return (
          subscriptionPrograms?.some((_sub) => program?.Subscription?.includes(_sub)) ||
          programSubscriptionIDs.includes(`${item.SubscriptionID}`)
        );
      }
    );

    if (programSubscription?.ESR) {
      return programSubscription.ESR;
    }

    // Fallback: search through all subscriptions for any subscription with a truthy ESR value
    const fallbackSubscription = subscriptions?.find((item: IClassSubscription) => item.ESR);

    return fallbackSubscription?.ESR ?? undefined;
  }

  return undefined;
}

/**
 * @param mentors
 * @returns A mapped string array of the mentors names
 */
export function mapPreceptorNames(mentors: Mentor[] | undefined, mentorTitle?: string): string[] | undefined {
  if (mentors) {
    return mentors.map((item) => {
      let role = item.role;

      if (role === "Mentor" && mentorTitle) {
        role = mentorTitle;
      }

      return `${item.name} - ${role}`;
    });
  }

  return undefined;
}

/**
 * @param regions
 * @returns A formatted string of the user's regions
 */
export function mapRegionNames(regions: string[] | undefined, programID: string | undefined): string | undefined {
  if ([PROGRAM_IDS.SWAST_SP_AP_RP, PROGRAM_IDS.NWAS_ARC].includes(programID ?? "")) {
    return undefined;
  }

  if (regions) {
    return regions.join(", ");
  }

  return undefined;
}

/**
 * Checks whether the user has completed their Preceptorship to show congratulations card
 * @returns true if the Preceptorship is complete
 */
export function isProgramCompleteNQP(
  progressChecks: ProgressCheck[] | undefined,
  allProgressCheckData: AllProgressCheckStatuses[] | undefined | null,
  allEvidence: IEvidence[],
  programID: string | undefined
): boolean {
  if (!progressChecks || !allProgressCheckData || !programID) {
    return false;
  }

  const dataForProgram: AllProgressCheckStatuses | undefined = allProgressCheckData.find(
    (item: any): item is AllProgressCheckStatuses => item.programID === programID
  );

  const completionProgressCheck = progressChecks.find((item) => item.ApprovalCompletesPM);

  if (completionProgressCheck) {
    const progressCheckStatus = dataForProgram?.pCs.find((item) => item.pCId === completionProgressCheck.ID);

    if (progressCheckStatus?.submissions.status === SubmissionState.Approved) {
      return true;
    }
  }

  for (let i = 0; i < progressChecks.length; i++) {
    const check = progressChecks[i];
    const progressCheckStatus = dataForProgram?.pCs.find((item) => item.pCId === check.ID);

    if (
      [`${SequentiallyLocked.LockedIgnore}`, `${SequentiallyLocked.UnlockedIgnore}`].includes(
        check.SequentiallyLocked ?? ""
      )
    ) {
      continue;
    }

    if (progressCheckStatus?.submissions.status === SubmissionState.Approved) {
      continue;
    }

    const progressCheckEvidence = getEvidenceForProgressCheck(allEvidence, check.ID);

    if (progressCheckEvidence?.length > 0) {
      const completed = isAllRequiredEvidenceAddedForProgressCheck(progressCheckEvidence, check["Competence/Activity"]);

      if (completed) {
        if (check.Approval === ProgressCheckApproval.NotRequired) {
          continue;
        } else if (progressCheckStatus?.submissions.status === SubmissionState.Submitted) {
          return false;
        } else {
          return false;
        }
      } else {
        if (
          [`${SequentiallyLocked.UnlockedIgnore}`, `${SequentiallyLocked.LockedIgnore}`].includes(
            check.SequentiallyLocked ?? ""
          )
        ) {
          continue;
        } else {
          return false;
        }
      }
    } else {
      return false;
    }
  }

  return true;
}

type CheckIfProgressCheckIsLockedParams = {
  program: ProgramData;
  progressChecks: ProgressCheck[] | undefined;
  allProgressCheckData: AllProgressCheckStatuses[] | undefined | null;
  allEvidence: IEvidence[];
  programID: string | undefined;
  programCycle: number | undefined;
};

export function checkIfProgressCheckIsLockedWithProgramCycle({
  program,
  progressChecks,
  allProgressCheckData,
  allEvidence,
  programID,
  programCycle,
}: CheckIfProgressCheckIsLockedParams): boolean | undefined {
  if (isSPAPRP(program.ID)) {
    const currentCycleEvidence = allEvidence.filter(
      (evidence) => evidence.evidenceJSON?.programInfo?.programCycle === programCycle
    );

    if (currentCycleEvidence.length === 0) {
      return undefined;
    }

    return isProgramCycleComplete({
      progressChecks: progressChecks,
      allProgressCheckData: allProgressCheckData,
      allEvidence: currentCycleEvidence,
      programID: programID,
      programCycle: programCycle,
    });
  }

  if (isNQPProgram(program.ID)) {
    return isProgramCompleteNQP(progressChecks, allProgressCheckData, allEvidence, programID);
  }

  return undefined;
}

type IsProgramCycleCompleteParams = {
  progressChecks: ProgressCheck[] | undefined;
  allProgressCheckData: AllProgressCheckStatuses[] | undefined | null;
  allEvidence: IEvidence[];
  programID: string | undefined;
  programCycle: number | undefined;
};

export function isProgramCycleComplete({
  progressChecks,
  allProgressCheckData,
  allEvidence,
  programID,
  programCycle,
}: IsProgramCycleCompleteParams): boolean {
  if (!progressChecks || !allProgressCheckData || !programID) {
    return false;
  }

  const dataForProgram: AllProgressCheckStatuses | undefined = allProgressCheckData.find(
    (item: any): item is AllProgressCheckStatuses => item.programID === programID
  );

  const completionProgressCheck = progressChecks.find((item) => item.ApprovalCompletesPM);

  if (completionProgressCheck) {
    const progressCheckStatus = dataForProgram?.pCs.find(
      (item) => item.pCId === completionProgressCheck.ID && item.submissions.cycle === programCycle
    );

    if (progressCheckStatus?.submissions.status === SubmissionState.Approved) {
      return true;
    }
  }

  for (let i = 0; i < progressChecks.length; i++) {
    const check = progressChecks[i];
    const progressCheckStatus = dataForProgram?.pCs.find(
      (item) => item.pCId === check.ID && item.submissions.cycle === programCycle
    );

    if (
      [`${SequentiallyLocked.LockedIgnore}`, `${SequentiallyLocked.UnlockedIgnore}`].includes(
        check.SequentiallyLocked ?? ""
      )
    ) {
      continue;
    }

    if (progressCheckStatus?.submissions.status === SubmissionState.Approved) {
      continue;
    }

    const progressCheckEvidence = getEvidenceForProgressCheck(allEvidence, check.ID);

    if (progressCheckEvidence?.length > 0) {
      const completed = isAllRequiredEvidenceAddedForProgressCheck(progressCheckEvidence, check["Competence/Activity"]);

      if (completed) {
        if (check.Approval === ProgressCheckApproval.NotRequired) {
          continue;
        } else if (progressCheckStatus?.submissions.status === SubmissionState.Submitted) {
          return false;
        } else {
          return false;
        }
      } else {
        if (
          [`${SequentiallyLocked.UnlockedIgnore}`, `${SequentiallyLocked.LockedIgnore}`].includes(
            check.SequentiallyLocked ?? ""
          )
        ) {
          continue;
        } else {
          return false;
        }
      }
    } else {
      return false;
    }
  }

  return true;
}

/**
 * Removes the progress check that the user has already added all required evidence for
 * @param progressChecks - The progress checks for the program module
 * @param competences - The progress check competences
 * @param program - The current program
 * @param programProgressCheckData - The array of progress check data for all programs
 * @returns The filtered progress check array
 */
export function removeProgressChecks(
  progressChecks: ProgressCheck[],
  allEvidence: IEvidence[],
  program: ProgramData | null,
  programProgressCheckData: AllProgressCheckStatuses[] | null,
  fastTrackActive: boolean,
  pauseAndHold: boolean,
  selectedProgressCheck?: ProgressCheck | null
): ProgressCheck[] {
  let result: ProgressCheck[] = [];

  const currentProgressCheckData =
    programProgressCheckData?.find((item: any): item is AllProgressCheckStatuses => item.programID === program?.ID) ??
    null;

  // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
  if (isMTS(program?.ID!) || isCHUB(program?.ID!) || isClinicalAdvisor(program?.ID!)) {
    result = progressChecks?.filter((progressCheck: ProgressCheck) => {
      const evidenceForProgressCheck =
        allEvidence?.filter((evidence: IEvidence) => {
          const evidenceJSON = evidence.evidenceJSON;
          const id = evidenceJSON?.programInfo?.progressCheckID || "";

          const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

          return progressCheck.ID === id && onHoldReset !== 1 && evidence.draft !== true;
        }) ?? [];

      return !isProgressCheckApprovedBasedOnCompTrigger(evidenceForProgressCheck, progressCheck["Competence/Activity"]);
    });

    // Remove locked progress checks - Either completed or approved
    result = result?.filter((progressCheck: ProgressCheck) => {
      const locked = checkIfProgressCheckIsLocked(
        progressCheck,
        progressChecks,
        allEvidence,
        currentProgressCheckData,
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        program?.ID!,
        fastTrackActive,
        pauseAndHold
      );

      return !locked;
    });
    return result;
  } else {
    const isNQP = isNQPProgram(program?.ID ?? "");

    // Remove progress checks with all evidence added
    result = progressChecks?.filter((progressCheck: ProgressCheck) => {
      const requiredCompetences = progressCheck["Competence/Activity"].filter((competence: CompetenceActivityClass) => {
        const isWellbeing = competence.isWellbeingActivity;

        if (isNQP && isWellbeing) {
          return !isWellbeing;
        }

        const hasNumberRequired = typeof competence["Number Evidences required"] === "number";

        return (
          !hasNumberRequired || (competence["Number Evidences required"] && competence["Number Evidences required"] > 0)
        );
      });

      // Filter for only required competences
      const progressCheckCompetenceIDs = requiredCompetences.map((item) => item.ID).sort();

      const evidenceForProgressCheck =
        allEvidence?.filter((evidence: IEvidence) => {
          const evidenceJSON = evidence.evidenceJSON;
          const id = evidenceJSON?.programInfo?.progressCheckID || "";

          const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

          return progressCheck.ID === id && onHoldReset !== 1 && evidence.draft !== true;
        }) ?? [];

      const competenceIDs = _.uniq(
        evidenceForProgressCheck
          .map((evidence: IEvidence) => {
            const evidenceJSON = evidence.evidenceJSON;
            return evidenceJSON?.programInfo?.progressCheckCompetenceID;
          })
          .sort()
      );

      const isSelectedProgressCheck = progressCheck.ID === selectedProgressCheck?.ID;

      const alwaysOpen = progressCheck.SequentiallyLocked === SequentiallyLocked.UnlockedIgnore;

      const keepOpen = !!progressCheck.KeepOpenOnCompletion;

      // console.log("isSelectedProgressCheck", isSelectedProgressCheck);
      return (
        alwaysOpen ||
        isSelectedProgressCheck ||
        (requiredCompetences.length > 0 && !_.isEqual(progressCheckCompetenceIDs, competenceIDs)) ||
        (requiredCompetences.length === 0 && !alwaysOpen) ||
        keepOpen
      );
    });

    // Remove locked progress checks - Either completed or approved
    result = result?.filter((progressCheck: ProgressCheck) => {
      const locked = checkIfProgressCheckIsLocked(
        progressCheck,
        progressChecks,
        allEvidence,
        currentProgressCheckData,
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        program?.ID!,
        fastTrackActive,
        pauseAndHold
      );

      const isSelectedProgressCheck = progressCheck.ID === selectedProgressCheck?.ID;

      return isSelectedProgressCheck || !locked;
    });

    result = result?.filter((progressCheck: ProgressCheck) => {
      let _competences = _.cloneDeep(progressCheck["Competence/Activity"]) ?? [];
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      _competences = removeProgressCheckCompetences(_competences, progressCheck, allEvidence, program?.ID!, null);

      const isSelectedProgressCheck = progressCheck.ID === selectedProgressCheck?.ID;

      return isSelectedProgressCheck || _competences.length > 0;
    });

    return result;
  }
}

/**
 * Removes the progress check competences that the user has already added evidence for
 * @param competences - The progress check competences
 * @param progressCheckID - The ID of the current progress check
 * @param allEvidence - All of the user's evidence
 * @returns The filtered competence array
 */
export function removeProgressCheckCompetences(
  competences: CompetenceActivityClass[],
  progressCheck: ProgressCheck,
  allEvidence: IEvidence[],
  programId: string,
  selectedProgressCheckCompetence: CompetenceActivityClass | null,
  fastTrackActive: boolean = false
): CompetenceActivityClass[] {
  let result: CompetenceActivityClass[] = [];

  const evidenceForProgressCheck =
    allEvidence?.filter((evidence: IEvidence) => {
      const evidenceJSON = evidence.evidenceJSON;
      const id = evidenceJSON?.programInfo?.progressCheckID ?? "";

      const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

      return progressCheck.ID === id && onHoldReset !== 1 && evidence.draft !== true;
    }) ?? [];

  const mappedCompetenceCheckIDs = evidenceForProgressCheck?.map((evidence: IEvidence) => {
    const evidenceJSON = evidence.evidenceJSON;
    const id = evidenceJSON?.programInfo?.progressCheckCompetenceID || "";

    return id;
  });

  result = competences?.filter((competence: CompetenceActivityClass) => {
    if (canHandleMultiplePiecesOfEvidencePerEvidenceType(programId)) {
      if (selectedProgressCheckCompetence && selectedProgressCheckCompetence.ID === competence.ID) return true;
      if (competence.isFinalReview && !isCSM(programId)) {
        const otherCompsDone = competences
          .filter((comp) => !comp.isFinalReview && comp["Number Evidences required"])
          .every((c) => {
            const evidenceStatus = EvidenceUtils.hasReachedEvidenceCount(evidenceForProgressCheck, c);
            const learningOutcomesStatus = EvidenceUtils.hasLearningOutcomes(evidenceForProgressCheck, c);
            if (
              progressCheck.ID === PROGRESS_CHECK_IDS.LAS_MTS_MENTORING ||
              progressCheck.ID === PROGRESS_CHECK_IDS.LAS_CHUB_MENTORING
            ) {
              if (
                (evidenceStatus === "COMPLETE" && learningOutcomesStatus === "COMPLETE_REQUIRED") ||
                (evidenceStatus === "COMPLETE" && learningOutcomesStatus === "COMPLETE_ALL") ||
                (evidenceStatus === "COMPLETE" && !c["Learning Objective"]?.length)
              )
                return true;
            } else {
              if (evidenceStatus === "COMPLETE") return true;
            }
            return false;
          });

        return otherCompsDone;
      }
      if (competence.isLockedByRequiredEvidence) {
        const otherCompsDone = competences
          .filter((comp) => !comp.isLockedByRequiredEvidence)
          .every((c) => {
            const evidenceStatus = EvidenceUtils.hasReachedEvidenceCount(evidenceForProgressCheck, c);

            if (evidenceStatus === "COMPLETE") return true;

            return false;
          });

        return otherCompsDone;
      }
      if (competence.FilledInBy === FilledInBy.Mentor) {
        return false;
      }

      const multipleAllowed = competence.MultipleAllowed;
      const requiredNumber = competence["Number Evidences required"] ?? 0;
      const evidenceForComp = evidenceForProgressCheck.filter((e) => {
        const evidenceJSON = e.evidenceJSON;
        const id = evidenceJSON?.programInfo?.progressCheckCompetenceID || "";
        return id === competence.ID;
      });
      let includeComp = false;
      if (multipleAllowed) {
        includeComp = true;
      } else {
        if (requiredNumber === 0) {
          includeComp = evidenceForComp.length < 1;
        } else {
          includeComp = evidenceForComp.length < requiredNumber;
        }
      }

      return includeComp;
    } else {
      const wellbeingCompetenceIncluded = !mappedCompetenceCheckIDs?.includes(competence.ID);
      const isCurrentCompetence = competence.ID === selectedProgressCheckCompetence?.ID;

      return isCurrentCompetence || wellbeingCompetenceIncluded;
    }
  });

  result = result.filter((competence: CompetenceActivityClass) => !competence.isWellbeingActivity);

  if (!fastTrackActive) {
    result = result.filter((competence: CompetenceActivityClass) => {
      return !competence.FastTrackEvidence;
    });
  }

  return result;
}

/**
 * Checks whether user details saved are for an EPRR program module.
 * @param programID - The current program ID
 * @returns
 */
export function saveUserDetailsForEPRRProgram(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_EPRR, PROGRAM_IDS.SWAST_EPRR, PROGRAM_IDS.NEAS_EPRR, PROGRAM_IDS.TEST_PROGRAM].includes(
    programID
  );
}

/**
 * Checks if the usage data should be calculated based on a competency and matching evidence
 * @param programID - The current program ID
 * @returns
 */
export function shouldCalculateUsageData(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_EPRR, PROGRAM_IDS.SWAST_EPRR, PROGRAM_IDS.NEAS_EPRR, PROGRAM_IDS.TEST_PROGRAM].includes(
    programID
  );
}

/**
 * Checks whether the read evidence view should get the progress check information
 * @param programID - The current program ID
 * @returns
 */
export function shouldGetProgressCheckInfo(programID: string): boolean {
  return [
    PROGRAM_IDS.SWAST_PRECEPTORSHIP,
    PROGRAM_IDS.SWAST_NQP2,
    PROGRAM_IDS.LAS_MTS,
    PROGRAM_IDS.LAS_CHUB,
    PROGRAM_IDS.LAS_CA,
    PROGRAM_IDS.NWAS_NQP,
    PROGRAM_IDS.NEAS_NQP,
    PROGRAM_IDS.LAS_CSM,
    PROGRAM_IDS.YAS_NQP,
    PROGRAM_IDS.LAS_NQP,
    PROGRAM_IDS.SWAST_SP_AP_RP,
    PROGRAM_IDS.UOC_APPRENTICESHIP,
  ].includes(programID);
}
/**
 * Checks whether the input date for supporting evidence should be mandatory.
 * @param programID - The current program ID
 * @returns
 */
export function shouldInputDateOnSupportingEvidenceBeMandatory(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_EPRR, PROGRAM_IDS.SWAST_EPRR, PROGRAM_IDS.NEAS_EPRR, PROGRAM_IDS.TEST_PROGRAM].includes(
    programID
  );
}

/**
 * Checks whether the certificate inputs should be shown after (or before) role selection.
 * @param programID - The current program ID
 * @returns
 */
export function showCertsAfterRoleSelection(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_EPRR, PROGRAM_IDS.NEAS_EPRR, PROGRAM_IDS.TEST_PROGRAM].includes(programID);
}

export function showConfidenceLevelGraph(programID: string): boolean {
  return [PROGRAM_IDS.SWAST_PRECEPTORSHIP, PROGRAM_IDS.NEAS_NQP, PROGRAM_IDS.YAS_NQP, PROGRAM_IDS.LAS_NQP].includes(
    programID
  );
}

/**
 * Checks whether the certificate description should be shown under the certificate input
 * @param programID - The current program ID
 * @returns
 */
export function showDescriptionForCertificate(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_EPRR, PROGRAM_IDS.SWAST_EPRR, PROGRAM_IDS.NEAS_EPRR, PROGRAM_IDS.TEST_PROGRAM].includes(
    programID
  );
}

export function showDeclarationOfObligationsTickbox(programID: string): boolean {
  return [PROGRAM_IDS.NEAS_EPRR, PROGRAM_IDS.TEST_PROGRAM].includes(programID);
}

/**
 * Checks whether the evidence block should be displayed
 * @param programID - The current program ID
 * @param user - The user object
 * @returns true if the Program Module should have the evidence list displayed in the Program Home page
 */
export function showEvidenceBlock(programID: string, user: any): boolean {
  if (user) {
    if (isNwasEPRR(programID) && !isNwasEprrDetailsSet(user)) return false;
    if (isSwastEPRR(programID) && !isSwastEprrDetailsSet(user)) return false;
    if (isNeasEPRR(programID) && !isNeasEprrDetailsSet(user)) return false;
    if (isTestEPRR(programID) && !isTestEprrDetailsSet(user)) return false;
    if (isMTS(programID) && !isMtsDetailsSet(user)) return false;
    if (isCHUB(programID) && !isChubDetailsSet(user)) return false;
    if (isCSM(programID) && !isCsmDetailsSet(user)) return false;
    if (isClinicalAdvisor(programID) && !isClinicalAdvisorsDetailsSet(user)) return false;
    if (isNwasARC(programID) && !isNwasARCDetailsSet(user)) return false;
    if (isSPAPRP(programID) && !isSPAPRPDetailsSet(user)) return false;
    if (hasSetupNQP(programID) && !isNQPDetailsSet(user, programID)) return false;
  }
  return [
    PROGRAM_IDS.SWAST_HART_EPRR,
    PROGRAM_IDS.SWAST_PRECEPTORSHIP,
    PROGRAM_IDS.NWAS_EPRR,
    PROGRAM_IDS.SWAST_EPRR,
    PROGRAM_IDS.SWAST_NQP2,
    PROGRAM_IDS.LAS_MTS,
    PROGRAM_IDS.LAS_CHUB,
    PROGRAM_IDS.LAS_CA,
    PROGRAM_IDS.NEAS_NQP,
    PROGRAM_IDS.NWAS_NQP,
    PROGRAM_IDS.LAS_CSM,
    PROGRAM_IDS.NEAS_EPRR,
    PROGRAM_IDS.YAS_NQP,
    PROGRAM_IDS.TEST_PROGRAM,
    PROGRAM_IDS.LAS_NQP,
    PROGRAM_IDS.NWAS_ARC,
    PROGRAM_IDS.SWAST_SP_AP_RP,
    PROGRAM_IDS.UOC_APPRENTICESHIP,
  ].includes(programID);
}

/**
 * Checks whether the evidence count should be displayed
 * @param programID - The current program ID
 * @returns
 */
export function showEvidenceCount(programID: string): boolean {
  return [PROGRAM_IDS.SWAST_HART_EPRR].includes(programID);
}

/**
 * Checks whether optional standards should be shown for a program in the program home page.
 * @param programID - The current program ID
 * @returns
 */
export function showMandatoryStandardsInProgramHomePage(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_EPRR, PROGRAM_IDS.SWAST_EPRR, PROGRAM_IDS.NEAS_EPRR, PROGRAM_IDS.TEST_PROGRAM].includes(
    programID
  );
}

export function showLearningObjectivesCard(programID: string, user: any): boolean {
  if (isSPAPRP(programID) && !isSPAPRPDetailsSet(user)) return false;
  if (isNwasARC(programID) && !isNwasARCDetailsSet(user)) return false;

  return [
    PROGRAM_IDS.NEAS_NQP,
    PROGRAM_IDS.NWAS_NQP,
    PROGRAM_IDS.YAS_NQP,
    PROGRAM_IDS.LAS_NQP,
    PROGRAM_IDS.SWAST_SP_AP_RP,
    PROGRAM_IDS.NWAS_ARC,
  ].includes(programID);
}

export function hasLearningObjectives(program: ProgramData, userRoles: string[]): boolean {
  let learningObjectives;

  if (userRoles.length > 0) {
    learningObjectives = getAllLearningObjectivesForRole(program, userRoles[0]);
  } else {
    learningObjectives = getAllLearningObjectivesForProgram(program);
  }

  return learningObjectives.length > 0;
}

export function showMedicalImagingCard(programID: string): boolean {
  return [PROGRAM_IDS.SWAST_SP_AP_RP].includes(programID);
}

export function showWPBACard(programID: string): boolean {
  return [PROGRAM_IDS.SWAST_SP_AP_RP].includes(programID);
}

export function getLearningObjectivesListHeaderText(program: ProgramData, userRoleName?: string): string {
  if (userRoleName) {
    const role = program.Roles?.find((role) => role.Name === userRoleName);

    if (role) {
      return role.LOsListHeaderText ?? "Overview of the LOs that you have met over your program.";
    }
  }

  return program.LOsListHeaderText ?? "Overview of the LOs that you have met over your program.";
}

export function getLearningObjectivesCardTitle(program: ProgramData, userRoleName?: string): string {
  if (userRoleName) {
    const role = program.Roles?.find((role) => role.Name === userRoleName);

    if (role) {
      return role.LOsListSubHeaderText ?? "Learning Outcomes met";
    }
  }

  const name = program.LearningOutcomesName;

  if (isSPAPRP(program.ID)) {
    return name && name.length > 0 ? `${name} achieved` : "Learning Outcomes met";
  } else if (isNwasARC(program.ID)) {
    return name && name.length > 0 ? `${name}` : "Learning Outcomes met";
  }

  return name && name.length > 0 ? `${name} met` : "Learning Outcomes met";
}

export function getSkillsCardTitle(title?: string): string {
  return title && title.length > 0 ? title : "Clinical Skills Log";
}

export function getSkillsCardSubTitle(subTitle?: string): string {
  return subTitle && subTitle.length > 0 ? subTitle : "Log your Clinical Skills here";
}

/**
 * Checks whether optional standards should be shown for a program in the program details page.
 * @param programID - The current program ID
 * @returns
 */
export function showOptionalStandardsInDetailsPage(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_EPRR, PROGRAM_IDS.SWAST_EPRR, PROGRAM_IDS.NEAS_EPRR, PROGRAM_IDS.TEST_PROGRAM].includes(
    programID
  );
}

export function showAppraisalDateInDetailsPage(programID: string): boolean {
  return [PROGRAM_IDS.SWAST_SP_AP_RP].includes(programID);
}

export function showStartDateInDetailsPage(program: ProgramData, programID: string): boolean {
  if (program.ShowNQPStartDate) {
    return true;
  }

  return [PROGRAM_IDS.LAS_NQP].includes(programID);
}

export function showStartDateInDetailsCard(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_ARC].includes(programID);
}

export function showNQPCourseInDetailsPage(program: ProgramData, programID: string): boolean {
  if (program.ShowNQPCourse) {
    return true;
  }

  return [PROGRAM_IDS.LAS_NQP].includes(programID);
}

/**
 * Checks whether the details card should be displayed
 * @param programID - The current program ID
 * @returns
 */
export function showProgramDetailsCard(programID: string): boolean {
  return [
    PROGRAM_IDS.SWAST_PRECEPTORSHIP,
    PROGRAM_IDS.SWAST_NQP2,
    PROGRAM_IDS.NEAS_NQP,
    PROGRAM_IDS.NWAS_NQP,
    PROGRAM_IDS.YAS_NQP,
    PROGRAM_IDS.LAS_NQP,
    PROGRAM_IDS.SWAST_SP_AP_RP,
    PROGRAM_IDS.UOC_APPRENTICESHIP,
  ].includes(programID);
}

export function showProgramDetailsEditButton(program: ProgramData): boolean {
  const hasRoles = (program.Roles && program.Roles?.length > 0) ?? false;
  const hasAreas = (program.Areas && program.Areas?.length > 0) ?? false;

  return hasRoles || hasAreas;
}

/**
 * Checks whether areas should be shown for a program.
 * @param programID - The current program ID
 * @returns
 */
export function showProgramAreas(programID: string): boolean {
  return [
    PROGRAM_IDS.NWAS_EPRR,
    PROGRAM_IDS.SWAST_EPRR,
    PROGRAM_IDS.NEAS_EPRR,
    PROGRAM_IDS.TEST_PROGRAM,
    PROGRAM_IDS.SWAST_SP_AP_RP,
    PROGRAM_IDS.NWAS_ARC,
  ].includes(programID);
}

export function showEvidenceButtonInLearningOutcomes(programID: string): boolean {
  return [PROGRAM_IDS.SWAST_SP_AP_RP, PROGRAM_IDS.NWAS_ARC].includes(programID);
}

export function showNQPAreas(programID: string): boolean {
  return [PROGRAM_IDS.NEAS_NQP, PROGRAM_IDS.NWAS_NQP, PROGRAM_IDS.LAS_NQP].includes(programID);
}

export function areNQPAreasMandatory(programID: string): boolean {
  return [PROGRAM_IDS.LAS_NQP].includes(programID);
}

/**
 * Checks whether the user's program certificates should be displayed
 * @param programID - The current program ID
 * @returns
 */
export function showProgramCertificates(programID: string): boolean {
  return [PROGRAM_IDS.SWAST_HART_EPRR].includes(programID);
}

/**
 * Checks whether a trust label should be shown on the DetailsContentCollapsible component.
 * @param programID - The current program ID
 * @returns
 */
export function showTrustLabelForDetailsCollapsible(programID: string): boolean {
  return [
    PROGRAM_IDS.NWAS_EPRR,
    PROGRAM_IDS.SWAST_EPRR,
    PROGRAM_IDS.LAS_MTS,
    PROGRAM_IDS.LAS_CHUB,
    PROGRAM_IDS.LAS_CA,
    PROGRAM_IDS.LAS_CSM,
    PROGRAM_IDS.NEAS_EPRR,
    PROGRAM_IDS.TEST_PROGRAM,
    PROGRAM_IDS.NWAS_ARC,
  ].includes(programID);
}

export function showProgressCheckProgressOnDashboard(programID: string): boolean {
  return [
    PROGRAM_IDS.SWAST_PRECEPTORSHIP,
    PROGRAM_IDS.SWAST_NQP2,
    PROGRAM_IDS.NEAS_NQP,
    PROGRAM_IDS.NWAS_NQP,
    PROGRAM_IDS.YAS_NQP,
    PROGRAM_IDS.LAS_NQP,
    PROGRAM_IDS.SWAST_SP_AP_RP,
  ].includes(programID);
}

/**
 * Checks whether the standards label should be shown on the ProgramEvidenceCard component (e.g. for NWAS IC).
 * @param programID - The current program ID
 * @returns
 */
export function showStandardsLabelForEvidenceCard(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_EPRR, PROGRAM_IDS.SWAST_EPRR, PROGRAM_IDS.NEAS_EPRR, PROGRAM_IDS.TEST_PROGRAM].includes(
    programID
  );
}

export function showPauseInfo(programModuleStatus: ProgramModuleStatus | undefined): boolean {
  switch (programModuleStatus?.data.Pause) {
    case "Pause":
    case "PauseAndHold":
    case "Extension":
      return true;
    default:
      return false;
  }
}

export function showFastTrackInfo(programModuleStaus: ProgramModuleStatus | undefined): boolean {
  return programModuleStaus?.data.FastTrack === "Enabled";
}

/**
 * Checks whether the Program Home page should display the old Details card (SWAST HART EPRR) or the new Details card (NWAS EPRR)
 * @param programID
 * @returns
 */
export function useDetailsCardInsteadOfProgramInfoCard(programID: string) {
  return [
    PROGRAM_IDS.NWAS_EPRR,
    PROGRAM_IDS.SWAST_EPRR,
    PROGRAM_IDS.LAS_MTS,
    PROGRAM_IDS.LAS_CHUB,
    PROGRAM_IDS.LAS_CA,
    PROGRAM_IDS.LAS_CSM,
    PROGRAM_IDS.NEAS_EPRR,
    PROGRAM_IDS.TEST_PROGRAM,
    PROGRAM_IDS.NWAS_ARC,
  ].includes(programID);
}

/**
 * Checks whether the details component should use radio buttons instead of checkboxes for selecting roles.
 * @param programID - The current program ID
 * @returns
 */
export function useRadioRoleSelector(programID: string): boolean {
  return [
    PROGRAM_IDS.NWAS_EPRR,
    PROGRAM_IDS.SWAST_EPRR,
    PROGRAM_IDS.NEAS_EPRR,
    PROGRAM_IDS.TEST_PROGRAM,
    PROGRAM_IDS.NWAS_ARC,
    PROGRAM_IDS.SWAST_SP_AP_RP,
  ].includes(programID);
}

/**
 * Checks whether the certificate input components should use names as the labels.
 * @param programID - The current program ID
 * @returns
 */
export function useNameAsCertLabel(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_EPRR, PROGRAM_IDS.SWAST_EPRR, PROGRAM_IDS.NEAS_EPRR, PROGRAM_IDS.TEST_PROGRAM].includes(
    programID
  );
}

export function showProgressCheckEvidenceCount(progressCheckID: string): boolean {
  return [
    PROGRESS_CHECK_IDS.SWAST_PRECEPTORSHIP_RECALL_DAY_REFLECTIONS,
    PROGRESS_CHECK_IDS.SWAST_PRECEPTORSHIP_BAND_6_REFLECTIONS,
  ].includes(progressCheckID);
}

export function getUsersRole(user: IUser, programID: string): string {
  const role = user.programRoles?.find((role) => role.programID === programID);
  if (role && role.programRoles.length) {
    return role.programRoles[0];
  }

  return "unset";
}

export function getUsersTeam(user: IUser, programID: string): string {
  const role = user.programRoles?.find((role) => role.programID === programID);
  if (role && role?.team) {
    return role.team;
  }

  return "unset";
}

export function getNQPUserTeam(user: IUser, programID: string): string | undefined {
  const role = user.programRoles?.find((role) => role.programID === programID);
  if (role && role?.team) {
    return role.team;
  }

  return undefined;
}

export function getUsersStartDate(user: IUser, programID: string): Date | undefined {
  const role = user.programRoles?.find((role) => role.programID === programID);
  if (role && role?.startDate) {
    return new Date(role.startDate);
  }

  return undefined;
}

export function getUsersAppraisalDate(user: IUser, programID: string): Date | undefined {
  const role = user.programRoles?.find((role) => role.programID === programID);
  if (role && role?.appraisalDate) {
    return new Date(role.appraisalDate);
  }

  return undefined;
}

export function getUsersCourseId(user: IUser, programID: string): string {
  const role = user.programRoles?.find((role) => role.programID === programID);
  if (role && role?.courseId) {
    return role.courseId;
  }

  return "unset";
}

export function getNQPUsersCourseId(user: IUser, programID: string): string | undefined {
  const role = user.programRoles?.find((role) => role.programID === programID);
  if (role && role?.courseId) {
    return role.courseId;
  }

  return undefined;
}

/**
 * Before NWAS EPRR was introduced, programRoles in the IUserProgramRoles interface was just a string[].
 * Now it is a more descriptive array of objects, so this function checks if a particular program is
 * using the old format, or the new format.
 * @param programID - The current program ID
 * @returns
 */
export function usingDescriptiveProgramRolesFormat(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_EPRR, PROGRAM_IDS.SWAST_EPRR, PROGRAM_IDS.NEAS_EPRR, PROGRAM_IDS.TEST_PROGRAM].includes(
    programID
  );
}

/**
 * Checks whether the program is a LAS TT program module (MTS, CHUB, or CSM)
 * @param programID - The current program ID
 * @returns
 */
export function isTelephoneTriageModule(programID: string): boolean {
  return [PROGRAM_IDS.LAS_MTS, PROGRAM_IDS.LAS_CHUB, PROGRAM_IDS.LAS_CSM, PROGRAM_IDS.LAS_CA].includes(programID);
}

/**
 * Checks whether the program is LAS MTS
 * @param programID - The current program ID
 * @returns
 */
export function isMTS(programID: string): boolean {
  return [PROGRAM_IDS.LAS_MTS].includes(programID);
}

/**
 * Checks whether the program is LAS CA
 * @param programID - The current program ID
 * @returns
 */
export function isClinicalAdvisor(programID: string): boolean {
  return [PROGRAM_IDS.LAS_CA].includes(programID);
}

/**
 * Checks whether the program is LAS CHUB
 * @param programID - The current program ID
 * @returns
 */
export function isCHUB(programID: string): boolean {
  return [PROGRAM_IDS.LAS_CHUB].includes(programID);
}

/**
 * Checks whether the program is LAS CSM
 * @param programID - The current program ID
 * @returns
 */
export function isCSM(programID: string): boolean {
  return [PROGRAM_IDS.LAS_CSM].includes(programID);
}

export function isSPAPRP(programID: string): boolean {
  return [PROGRAM_IDS.SWAST_SP_AP_RP].includes(programID);
}

export function showLearningOutcomesListRowsAsButtons(programID: string): boolean {
  return [PROGRAM_IDS.SWAST_SP_AP_RP, PROGRAM_IDS.NWAS_ARC].includes(programID);
}

/**
 * Determines if special checks should be done when a user is editing their role details for an EPRR program module.
 * @param programID - The current program ID
 * @returns
 */
export function validateDetailsForEprr(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_EPRR, PROGRAM_IDS.SWAST_EPRR, PROGRAM_IDS.NEAS_EPRR, PROGRAM_IDS.TEST_PROGRAM].includes(
    programID
  );
}

export function validateDetailsForSPAPRP(programID: string): boolean {
  return [PROGRAM_IDS.SWAST_SP_AP_RP].includes(programID);
}

export function validateDetailsForNWASARC(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_ARC].includes(programID);
}

export function showCertificatesInProgramHomePage(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_ARC, PROGRAM_IDS.SWAST_EPRR, PROGRAM_IDS.NWAS_EPRR].includes(programID);
}

export function showSkillsInProgramHomePage(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_ARC].includes(programID);
}

export function userRoleHasCertificates(userRoles: string[], program: ProgramData): boolean {
  const hasCertificates = userRoles.some((role) =>
    program.Roles?.some((roles) => roles.Name === role && roles.Certificates && roles.Certificates?.length > 0)
  );

  return hasCertificates;
}

export function userRoleHasSkills(userRoles: string[], program: ProgramData): boolean {
  const hasSkills = userRoles.some((role) =>
    program.Roles?.some((roles) => roles.Name === role && roles.Skills && roles.Skills?.length > 0)
  );

  return hasSkills;
}

export function programHasSkills(program: ProgramData): boolean {
  return (program.Skills && program.Skills.length > 0) ?? false;
}

export function getSkillLevelForProgram(user: IUser | null | undefined, program: ProgramData): string {
  const programRole = user?.programRoles?.find((role) => role.programID === program.ID);
  return programRole?.level || program.Levels?.[0] || "";
}

export function filterSkillsByLevel(skills: ProgramSkill[], level: string): ProgramSkill[] {
  return skills.filter((skill) => !skill.Level || skill.Level === level);
}

const UOC_ROPP_COMPETENCY_LEVEL_NAMES = ["Asynchronous Competency Level", "Synchronous Competency Level"];
const UOC_CAP_COMPETENCY_LEVEL_NAMES = ["CAP Asynchronous Competency Level", "CAP Synchronous Competency Level"];
const UOC_ASSESSMENT_TYPE_NAMES = [
  "Asynchronous Assessment Type",
  "Synchronous Assessment Type",
  "CAP Asynchronous Assessment Type",
  "CAP Synchronous Assessment Type",
];

export function getUOCFilteredCompetencyOptions(
  evidencePart: EvidencePart,
  evidenceJSON: Record<string, any>,
  program: ProgramData | null | undefined,
  user: IUser | null | undefined
): string | null {
  if (!isUOCApprenticeship(program?.ID ?? "")) return null;

  const isROPPCompetencyLevel = UOC_ROPP_COMPETENCY_LEVEL_NAMES.includes(evidencePart.Name);
  const isCAPCompetencyLevel = UOC_CAP_COMPETENCY_LEVEL_NAMES.includes(evidencePart.Name);

  if (!isROPPCompetencyLevel && !isCAPCompetencyLevel) return null;

  const assessmentType = UOC_ASSESSMENT_TYPE_NAMES.reduce<string>(
    (found, name) => found || (evidenceJSON[name] as string) || "",
    ""
  );

  if (!assessmentType || assessmentType === "Formative") return null;

  if (isCAPCompetencyLevel) return "Independent";

  const userLevel = getSkillLevelForProgram(user, program!);
  return userLevel.includes("5") ? "Supervised\nIndependent" : "Independent";
}

export function getSkillEvidenceDefinitionForSlug(
  program: ProgramData,
  skillSlug: string
): EvidenceDefinition | undefined {
  return program["Evidence Definitions"]?.find((item) => item.EvidenceSelectionType === skillSlug);
}

const UOC_PED_NAME_FIELDS = [
  "Synchronous PE Name",
  "Asynchronous PE Name",
  "CAP Synchronous PE Name",
  "CAP Asynchronous PE Name",
  "Service Review Synchronous PE Name",
  "Service Review Asynchronous PE name",
  "Action Plan Synchronous PEd name",
  "Action Plan Asynchronous PEd name",
  "End of Year Review Synchronous Manager Name",
  "End of Year Review Asynchronous Manager Name",
  "Synchronous Manager name",
  "Placement Meeting Asynchronous Manager name",
];

export function getUOCPEdName(evidence: IEvidence): string | null {
  if (!isUOCApprenticeship(evidence.evidenceJSON?.programInfo?.programID ?? "")) return null;
  const json = evidence.evidenceJSON ?? {};
  for (const key of UOC_PED_NAME_FIELDS) {
    if (json[key]) return json[key] as string;
  }
  return null;
}

export function getUOCPEdEmail(evidence: IEvidence): string | null {
  if (!isUOCApprenticeship(evidence.evidenceJSON?.programInfo?.programID ?? "")) return null;
  const json = evidence.evidenceJSON ?? {};
  for (const [key, value] of Object.entries(json)) {
    if (
      key !== "programInfo" &&
      key.toLowerCase().includes("email") &&
      typeof value === "string" &&
      value.includes("@")
    ) {
      return value;
    }
  }
  return null;
}

export function getUOCPEdReviewDate(evidence: IEvidence): string | null {
  if (!isUOCApprenticeship(evidence.evidenceJSON?.programInfo?.programID ?? "")) return null;

  const evidenceJSON = evidence.evidenceJSON ?? {};
  const assuranceTypeEntry = Object.entries(evidenceJSON).find(
    ([key, value]) => key.endsWith(" Assurance Type") && typeof value === "string"
  );
  const assuranceType = assuranceTypeEntry?.[1] as string | undefined;

  if (!assuranceType) return null;

  if (assuranceType.includes("(synchronous)")) {
    return evidence.createdAt ?? null;
  }

  if (assuranceType.includes("(asynchronous)")) {
    const approvalState = evidenceJSON.programInfo?.approvalState;
    if (approvalState === "Approved" || approvalState === "Rejected") {
      return evidence.updatedAt ?? null;
    }
  }

  return null;
}

export function getSkillEvidenceKey(program: ProgramData): string | undefined {
  const evidenceDefinition = program["Evidence Definitions"]?.[0];

  if (evidenceDefinition) {
    const evidenceParts = evidenceDefinition["Evidence parts"];

    if (evidenceParts) {
      const skillEvidencePart = evidenceParts.find((part) => part["Field type"] === "SkillsSelect");

      if (skillEvidencePart) {
        return skillEvidencePart.Name;
      }
    }
  }

  return undefined;
}

export function getEvidenceType(params: {
  evidenceJSON: any;
  usesAdditionalEvidenceDefinitions: boolean;
  evidenceDefinitions: EvidenceDefinition[];
  formEvidenceDefinitions?: EvidenceDefinition[];
}): string {
  const { evidenceJSON, usesAdditionalEvidenceDefinitions, evidenceDefinitions, formEvidenceDefinitions } = params;

  if (usesAdditionalEvidenceDefinitions) {
    const evidenceDefinition = evidenceDefinitions.find(
      (item) => item.ID === evidenceJSON.programInfo.programEvidenceDefinitionID
    );

    const formEvidenceDefinition = formEvidenceDefinitions?.find(
      (item) => item.ID === evidenceJSON.programInfo.programEvidenceDefinitionID
    );

    if (evidenceDefinition && evidenceDefinition.EvidenceSelectionType) {
      return evidenceDefinition.EvidenceSelectionType;
    }

    if (formEvidenceDefinition && formEvidenceDefinition.EvidenceSelectionType) {
      return formEvidenceDefinition.EvidenceSelectionType;
    }
  }

  // TODO: Fix this when working on NWAS SCIL or SWAST SP/AP/RP
  // commented out for now to fix NWAS / NEAS EPRR editing
  // if (evidenceJSON.programInfo.progressCheckID === undefined) {
  //   return "skills";
  // }

  return "standard";
}

export function getEvidenceTypeString(program: ProgramData | undefined, evidenceType: string): string | null {
  if (!program) {
    return null;
  }

  if (program.ID === PROGRAM_IDS.SWAST_SP_AP_RP) {
    switch (evidenceType) {
      case "skills":
        return program.SkillsTitle ?? "Skills";
      case "standard":
        return null;
      case "capabilities":
        return program.LearningOutcomesName ?? "Capabilities";
      case "medical-imaging":
        return "Medical Imaging";
      case "wpba-supervision-form":
        return "WPBA Supervision Form";
      default:
        return null;
    }
  } else if (program.ID === PROGRAM_IDS.NWAS_ARC) {
    switch (evidenceType) {
      case "capabilities":
        return program.LearningOutcomesName ?? "Capabilities";
      case "witness-simulation":
        return "Witness Simulation";
      default:
        return null;
    }
  }

  return null;
}

export function getPreselectedSkillForCompetence(
  competence: CompetenceActivityClass | undefined,
  skills: ProgramSkill[]
): ProgramSkill | undefined {
  if (competence?.PreselectedSkillID && skills.length > 0) {
    return skills.find((skill) => skill.ID === competence.PreselectedSkillID);
  }

  return undefined;
}

export function getAddProgramEvidenceType(programID: string): { type?: "skill" | "standard" } {
  if (isNwasARC(programID)) {
    return { type: "skill" };
  }

  return { type: "standard" };
}

export function hideCertificatesInDetailsCard(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_ARC].includes(programID);
}

export function programHasCycles(programID: string): boolean {
  return [PROGRAM_IDS.SWAST_SP_AP_RP].includes(programID);
}

export function getLearningOutcomesFromRole(programID: string = ""): boolean {
  return [PROGRAM_IDS.SWAST_SP_AP_RP, PROGRAM_IDS.NWAS_ARC].includes(programID);
}

export function showCertificatesAtBottomOfProgramHomePage(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_ARC].includes(programID);
}

export function showCertificatesAtTopOfProgramHomePage(programID: string): boolean {
  return [PROGRAM_IDS.SWAST_EPRR, PROGRAM_IDS.NWAS_EPRR].includes(programID);
}

export function getAllSkillsFromRoles(program: ProgramData | undefined): ProgramSkill[] {
  if (!program) return [];

  const roles = program.Roles ?? [];
  const skills = roles.flatMap((role) => role.Skills).filter((skill) => skill !== undefined) as ProgramSkill[];

  const uniqueSkills = _.uniqBy(skills, "ID");

  return uniqueSkills;
}

export function hideStartDateInDetailsCard(): boolean | undefined {
  return undefined;
}

export function getCertificateMode(
  certificateType?: "pdf upload" | "External" | "Internal" | "External/Internal"
): "certificate" | "verification" {
  if (certificateType) {
    switch (certificateType) {
      case "Internal":
        return "verification";
      case "pdf upload":
      case "External":
      case "External/Internal":
        return "certificate";
      default:
        return "certificate";
    }
  }

  return "certificate";
}

export function showCertificateModeToggle(
  certificateType?: "pdf upload" | "External" | "Internal" | "External/Internal"
): boolean {
  switch (certificateType) {
    case "External":
    case "Internal":
    case "pdf upload":
      return false;
    case "External/Internal":
      return true;
    default:
      return false;
  }
}

export function shouldPromptAddCertificateToEvidence(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_EPRR, PROGRAM_IDS.SWAST_EPRR].includes(programID);
}

export function buildProgramEvidenceModalData(
  user: IUser | null,
  program: ProgramData,
  options?: { preselectedTitle?: string }
) {
  const evidenceDefinitions = program["Evidence Definitions"] ?? [];
  const usesAdditionalEvidenceDefinitions = program.UsesAdditionalEvidenceDefinitions;
  const mandatoryStandards = user ? getMandatoryStandards(user, program) : [];
  const optionalStandards = user ? getOptionalStandards(user, program) : [];
  const skills = user ? getSkillsForRole(user, program) : [];

  return {
    program,
    ...(evidenceDefinitions.length > 0 &&
      !usesAdditionalEvidenceDefinitions && {
        evidenceDefinitions: evidenceDefinitions[0],
      }),
    ...(program.ProgressChecks?.length && { progressChecks: program.ProgressChecks }),
    ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
    ...(optionalStandards.length > 0 && { optionalStandards }),
    ...(skills.length > 0 && { skills }),
    skill: null,
    ...(options?.preselectedTitle && { preselectedTitle: options.preselectedTitle }),
  };
}

export function getTrustBackgroundColor(programID: string): string {
  switch (programID) {
    case PROGRAM_IDS.NWAS_EPRR:
    case PROGRAM_IDS.NWAS_ARC:
      return "!bg-NWAS";
    case PROGRAM_IDS.LAS_MTS:
    case PROGRAM_IDS.LAS_CHUB:
    case PROGRAM_IDS.LAS_CSM:
    case PROGRAM_IDS.LAS_CA:
      return "!bg-LAS";
    case PROGRAM_IDS.NEAS_EPRR:
    case PROGRAM_IDS.TEST_PROGRAM:
      return "!bg-NEAS";
    case PROGRAM_IDS.SWAST_EPRR:
      return "!bg-SWASFT";
    default:
      return "!bg-black";
  }
}

export function getResendEvidenceSubtitle(programID?: string): string {
  switch (programID) {
    case PROGRAM_IDS.SWAST_SP_AP_RP:
      return "The link you sent";
    default:
      return "The link you sent";
  }
}

export function resendEvidenceText(): string {
  return "Remind";
}

export function getProgressCheckListTitle(programID: string): string {
  switch (programID) {
    case PROGRAM_IDS.UOC_APPRENTICESHIP:
      return "Meetings and Feedback";
    default:
      return "Progress checks";
  }
}
